import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
  ReactNode,
} from "react";
import { bindSelectedFeImages } from "./feImageTableBinding";
import {
  EditorContent,
  useEditor,
  generateJSON,
  generateHTML,
} from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TextAlign from "@tiptap/extension-text-align";
import { Table, TableCell } from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableHeader from "@tiptap/extension-table-header";
import Underline from "@tiptap/extension-underline";
import Paragraph from "@tiptap/extension-paragraph";
import Color from "@tiptap/extension-color";
import { TextStyle } from "@tiptap/extension-text-style";
import FontFamily from "@tiptap/extension-font-family";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Subscript from "@tiptap/extension-subscript";

import TipTapToolbar from "./TipTapToolbar";
import { TipTapTitleBar } from "./TipTapTitleBar";
import ResizableImage from "./ResizableImage";
import LSDBButton from "./LSDBButton";
import { FontSize } from "../../../../../data/FontSize";
import {
  useTipTapManualSave,
  useWriterAddComments,
} from "../../../projectManagementMutations";
import { useFetchContext } from "../../../../common/FetchContext";
import {
  useAvailableCheckList,
  useContinueReportWriting,
  useCreateReport,
  useDeleteDocComments,
  useGetVerifierComments,
  useMoveReport,
  usePendingCheckList,
  useReportIntakeImages,
} from "../../../projectManagementQueries";
import Dropcursor from "@tiptap/extension-dropcursor";
import Highlight from "@tiptap/extension-highlight";
import {
  Badge,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  Grid,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from "@material-ui/core";
import { CommandProps, Node, mergeAttributes } from "@tiptap/core";
import Link from "@tiptap/extension-link";
import { LineHeight } from "@tiptap/extension-text-style"; //line height import
import { SidePanel } from "../../writersAgendaV2/ReportView";
import { useReportTransitions } from "../../writersAgendaV2/useReportTransitions";
import { useAgendaState } from "../../writersAgendaV2/useAgendaState";
import { Role, Row, StatusKey } from "../../writersAgendaV2/types";
import ForumIcon from "@material-ui/icons/Forum";
import { MI } from "../../writersAgendaV2/icons";
import clsx from "clsx";
import moment from "moment";
import { useStyles } from "../../writersAgendaV2/styles";
import { COLORS } from "../../writersAgendaV2/theme";
import { urls } from "../../../../common/urls";
import { TextSelection } from "@tiptap/pm/state";
import { CellSelection } from "@tiptap/pm/tables";
import DeleteIcon from "@material-ui/icons/Delete";
import { FindReplaceWidget } from "./FindReplaceWidget";
import {
  transformJsonToPageHtmlChunks,
  REPORT_INFO_TABLE_MARKER,
  splitTopLevelNodes,
  packNodesIntoPages,
  isEffectivelyEmpty,
} from "./reportPageGenerator";

import { CustomHeading } from "./CustomHeading";
import {
  scrollFromTocLine,
  scrollToHeading,
  scrollToHeadingByTitle,
  normalizeTitle,
  findTocBlockRanges,
  isForcedPageStartText,
} from "./headingUtils";
import { extractTOCItems, TableOfContents, TOCItem } from "./TableOfContents";

interface PageSetup {
  widthInches: number;
  heightInches: number;
  marginTop: number;
  marginBottom: number;
  marginLeft: number;
  marginRight: number;
}

type CommentType = "text" | "table" | "image";

type Comment = {
  id: string;
  from: number;
  to: number;
  type: CommentType;
  selectedText: string;
  comment: string;
  createdAt: string;
};

interface HeaderConfig {
  label: string;
  src?: string;
  separatorColor?: string;
}

interface FooterConfig {
  left: string;
  center: string;
  separatorColor?: string;
  showPageNumbers?: boolean;
}

interface ChromeConfig {
  header: HeaderConfig;
  footer: FooterConfig;
  pageSetup: PageSetup;
  differentFirstPage?: boolean;
}

interface PageChromeProps {
  chrome: ChromeConfig;
  contentHeightPx: number;
  pageNumber: number;
  totalPages: number;
  isFirstPage: boolean; // now only controls header/footer visibility
  isCoverPage?: boolean; // NEW: triggers the hardcoded Kiwa cover design
  children?: ReactNode;
  customHeaderHtml?: string; // <--- ADD THIS
  customFooterHtml?: string; // <--- ADD THIS
}

interface SinglePageEditorProps {
  contentHtml: string;
  maxHeightPx: number;
  onChange: (html: string) => void;
  onFocus: (editorInstance: any) => void;
  onUserEdit: () => void;
  onOverflow: (overflowHtml: string) => void;
}

// ─────────────────────────────────────────────────────────────
// 1. PAGE CHROME COMPONENT
// ─────────────────────────────────────────────────────────────

export function PageChrome({
  chrome,
  pageNumber,
  totalPages,
  isFirstPage,
  children,
  contentHeightPx,
  customHeaderHtml, // <--- ADD THIS
  customFooterHtml, // <--- ADD THIS
}: PageChromeProps): JSX.Element {
  const { header, footer, pageSetup, differentFirstPage } = chrome;

  const pageWidthPx = pageSetup.widthInches * 96;
  const pageHeightPx = pageSetup.heightInches * 96;
  const marginTopPx = pageSetup.marginTop * 96;
  const marginBottomPx = pageSetup.marginBottom * 96;
  const marginLeftPx = pageSetup.marginLeft * 96;
  const marginRightPx = pageSetup.marginRight * 96;

  // // 👇 ADD HERE
  // console.log("header.logoDataUri:", header?.src);
  // console.log("customHeaderHtml:", customHeaderHtml);

  const getImageSrc = (src?: string) => {
    if (!src) return "";

    // Already a valid URL
    if (src.startsWith("http") || src.startsWith("data:")) {
      return src;
    }

    // SVG Base64
    if (src.startsWith("PHN2Z")) {
      return `data:image/svg+xml;base64,${src}`;
    }

    // PNG Base64
    if (src.startsWith("iVBOR")) {
      return `data:image/png;base64,${src}`;
    }

    // JPG Base64
    if (src.startsWith("/9j/")) {
      return `data:image/jpeg;base64,${src}`;
    }

    return src;
  };

  const showChrome = !(isFirstPage && differentFirstPage);

  return (
    <div
      className="docgen-page"
      style={{
        width: pageWidthPx,
        minHeight: pageHeightPx,
        background: "#fff",
        border: "1px solid #e4e2dc",
        borderRadius: 2,
        display: "flex",
        flexDirection: "column",
        position: "relative",
        boxSizing: "border-box",
        marginBottom: 32,
        overflow: "hidden",
      }}
    >
      {showChrome && (
        <div
          className="docgen-page-header"
          style={{
            padding: `8px ${marginRightPx}px 16px ${marginLeftPx}px`,
            display: customHeaderHtml ? "block" : "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
            borderBottom: `2px solid ${header?.separatorColor || "#095ae8"}`,
            flexShrink: 0,
            background: "#fff",
            zIndex: 10,
          }}
        >
          {customHeaderHtml ? (
            <div
              className="ProseMirror"
              dangerouslySetInnerHTML={{ __html: customHeaderHtml }}
              style={{ width: "100%", minHeight: "auto" }}
            />
          ) : (
            <>
              <span
                style={{
                  fontFamily: "Arial, sans-serif",
                  fontSize: 10,
                  fontStyle: "italic",
                  color: "#888",
                }}
              >
                {header?.label}
              </span>

              {header?.src && (
                <img
                  src={getImageSrc(header?.src)}
                  alt="Logo"
                  style={{
                    width: 150,
                    height: 40,
                    objectFit: "contain",
                  }}
                  onLoad={() => console.log("Loaded")}
                  onError={(e) => {
                    console.log("Image failed");
                    console.log((e.target as HTMLImageElement).src);
                  }}
                />
              )}
            </>
          )}
        </div>
      )}

      <div
        className="docgen-page-content"
        style={{
          maxHeight: contentHeightPx,
          minHeight: contentHeightPx,
          padding: "20px 80px 20px 80px",
          boxSizing: "border-box",
        }}
      >
        {children}
      </div>

      {showChrome && (
        <div
          className="docgen-page-footer"
          style={{
            marginTop: "auto",
            padding: `12px ${marginRightPx}px 16px ${marginLeftPx}px`,
            display: customFooterHtml ? "block" : "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderTop: `2px solid ${footer?.separatorColor || "#095ae8"}`,
            flexShrink: 0,
            fontFamily: "Arial, sans-serif",
            fontSize: 9,
            color: "#888",
            background: "#fff",
            zIndex: 10,
          }}
        >
          {customFooterHtml ? (
            <div
              className="ProseMirror"
              dangerouslySetInnerHTML={{
                __html: customFooterHtml
                  .replace(
                    /(<span[^>]*class="page-number-node"[^>]*>).*?(<\/span>)/g,
                    `$1${pageNumber}$2`,
                  )
                  .replace(
                    /(<span[^>]*class="total-pages-node"[^>]*>).*?(<\/span>)/g,
                    `$1${totalPages}$2`,
                  ),
              }}
              style={{ width: "100%", minHeight: "auto" }}
            />
          ) : (
            <>
              <span>{footer?.left}</span>
              <span>{footer?.center}</span>
              {footer?.showPageNumbers && (
                <span>
                  Page {pageNumber} | {totalPages}
                </span>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 2. JSON TRANSFORMER FUNCTIONS
// ─────────────────────────────────────────────────────────────
const FILE_SIGNATURES: Record<string, string> = {
  PHN2Z: "image/svg+xml",
  iVBOR: "image/png",
  "/9j/": "image/jpeg",
  R0lGOD: "image/gif",
  UklGR: "image/webp",
};

const normalizeImageSrc = (src?: string): string => {
  if (!src) return "";
  if (src.startsWith("http") || src.startsWith("data:")) return src;
  for (const prefix of Object.keys(FILE_SIGNATURES)) {
    if (src.startsWith(prefix)) {
      return `data:${FILE_SIGNATURES[prefix]};base64,${src}`;
    }
  }
  return src;
};

const normalizeImageSrcsInJson = (node: any): any => {
  if (Array.isArray(node)) {
    return node.map(normalizeImageSrcsInJson);
  }
  if (node !== null && typeof node === "object") {
    const newNode = { ...node };
    if (newNode.type === "image" && newNode.attrs?.src) {
      newNode.attrs = {
        ...newNode.attrs,
        src: normalizeImageSrc(newNode.attrs.src),
      };
    }
    if (newNode.content) {
      newNode.content = normalizeImageSrcsInJson(newNode.content);
    }
    return newNode;
  }
  return node;
};
// ─────────────────────────────────────────────────────────────
// 2. JSON TRANSFORMER FUNCTIONS
// ─────────────────────────────────────────────────────────────
const sanitizeTipTapJSON = (node: any): any => {
  if (Array.isArray(node)) {
    return node.map(sanitizeTipTapJSON).filter(Boolean); // Filter out any nulls returned by empty text nodes
  }

  if (node !== null && typeof node === "object") {
    // TipTap STRICTLY forbids text nodes with empty strings
    if (node.type === "text" && (!node.text || node.text === "")) {
      return null;
    }

    const newNode = { ...node };

    // Recursively clean child content
    if (newNode.content) {
      newNode.content = sanitizeTipTapJSON(newNode.content);
      // If a block ends up with an empty content array, remove the array entirely
      if (newNode.content.length === 0) {
        delete newNode.content;
      }
    }

    return newNode;
  }

  return node;
};

const extractParagraph = (node: any) => {
  // Check for standard images embedded inside a paragraph
  if (node.content?.length === 1 && node.content[0].type === "image") {
    const image = node.content[0];
    return {
      type: "image",
      blob_url: image.attrs.src,
      width_px: Number(image.attrs.width),
      height_px: Number(image.attrs.height),
    };
  }

  // Standard text runs extraction
  const runs =
    node.content?.map((textNode: any) => {
      const marks = textNode.marks || [];
      const textStyle = marks.find((m: any) => m.type === "textStyle");
      const highlightMark = marks.find((m: any) => m.type === "highlight");

      return {
        text: textNode.text ?? "",
        bold: marks.some((m: any) => m.type === "bold") || null,
        italic: marks.some((m: any) => m.type === "italic") || null,
        underline: marks.some((m: any) => m.type === "underline") || null,
        strike: marks.some((m: any) => m.type === "strike") || null,
        subscript: marks.some((m: any) => m.type === "subscript") || null,
        link: marks.find((m: any) => m.type === "link")?.attrs || null,
        color: textStyle?.attrs?.color?.replace("#", "") ?? null,
        font_size: textStyle?.attrs?.fontSize
          ? textStyle.attrs.fontSize.replace("pt", "").replace("px", "")
          : null,
        font_family: textStyle?.attrs?.fontFamily ?? null,
        highlight: highlightMark?.attrs?.color ?? null,
      };
    }) ?? [];

  return {
    type: "paragraph",
    alignment: node.attrs?.textAlign ?? "left",
    runs,
  };
};

const CustomTableCell = TableCell.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      backgroundColor: {
        default: null,
        parseHTML: (element) => element.style.backgroundColor || null,
        renderHTML: (attributes) => {
          if (!attributes.backgroundColor) return {};
          return { style: `background-color: ${attributes.backgroundColor}` };
        },
      },
      textAlign: {
        default: null,
        parseHTML: (element) => element.style.textAlign || null,
        renderHTML: (attributes) => {
          if (!attributes.textAlign) return {};
          return { style: `text-align: ${attributes.textAlign}` };
        },
      },
    };
  },
});

const CustomTableHeader = TableHeader.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      backgroundColor: {
        default: null,
        parseHTML: (element) => element.style.backgroundColor || null,
        renderHTML: (attributes) => {
          if (!attributes.backgroundColor) return {};
          return { style: `background-color: ${attributes.backgroundColor}` };
        },
      },
      textAlign: {
        default: null,
        parseHTML: (element) => element.style.textAlign || null,
        renderHTML: (attributes) => {
          if (!attributes.textAlign) return {};
          return { style: `text-align: ${attributes.textAlign}` };
        },
      },
      textDirection: {
        default: null,

        parseHTML: (element) => element.getAttribute("data-text-direction"),
        renderHTML: (attributes) => {
          if (!attributes.textDirection) {
            return {};
          }
          return {
            "data-text-direction": attributes.textDirection,
          };
        },
      },
    };
  },
});

const CustomHeader = Node.create({
  name: "header",
  group: "block",
  content: "block+", // <--- Changed from 'inline*' to 'block+' so tables are allowed
  addAttributes() {
    return {
      textAlign: {
        default: null,
        parseHTML: (element) => element.style.textAlign || null,
        renderHTML: (attributes) => {
          if (!attributes.textAlign) return {};
          return { style: `text-align: ${attributes.textAlign}` };
        },
      },
    };
  },
  parseHTML() {
    return [{ tag: "h2" }, { tag: "header" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["header", mergeAttributes(HTMLAttributes), 0]; // Changed wrapper to 'header' to match tag semantics
  },
});

const PageNumberExtension = Node.create({
  name: "pageNumber",
  group: "inline",
  inline: true,

  addAttributes() {
    return {
      format: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-format") || null,
        renderHTML: (attributes) => {
          if (!attributes.format) return {};
          return { "data-format": attributes.format };
        },
      },
    };
  },

  parseHTML() {
    return [{ tag: "span.page-number-node" }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "span",
      mergeAttributes(HTMLAttributes, { class: "page-number-node" }),
      "#", // Visual placeholder in editor
    ];
  },
});

const TotalPagesExtension = Node.create({
  name: "totalPages",
  group: "inline",
  inline: true,

  addAttributes() {
    return {
      format: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-format") || null,
        renderHTML: (attributes) => {
          if (!attributes.format) return {};
          return { "data-format": attributes.format };
        },
      },
    };
  },

  parseHTML() {
    return [{ tag: "span.total-pages-node" }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "span",
      mergeAttributes(HTMLAttributes, { class: "total-pages-node" }),
      "##", // Visual placeholder in the editor
    ];
  },
});

const CustomFooter = Node.create({
  name: "footer",
  group: "block",
  content: "block+", // <--- Changed from 'inline*' to 'block+' so tables are allowed

  addAttributes() {
    return {
      textAlign: {
        default: null,
        parseHTML: (element) => element.style.textAlign || null,
        renderHTML: (attributes) => {
          if (!attributes.textAlign) return {};
          return { style: `text-align: ${attributes.textAlign}` };
        },
      },
    };
  },

  parseHTML() {
    return [{ tag: "footer" }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["footer", mergeAttributes(HTMLAttributes), 0];
  },
});

const CommentIconExtension = Node.create({
  name: "commentIcon",
  group: "inline",
  inline: true,
  atom: true, // Prevents users from typing inside the icon

  addAttributes() {
    return {
      "data-comment-id": {
        default: null,
        parseHTML: (element) => element.getAttribute("data-comment-id") || null,
        renderHTML: (attributes) => {
          if (!attributes["data-comment-id"]) return {};
          return { "data-comment-id": attributes["data-comment-id"] };
        },
      },
    };
  },

  parseHTML() {
    return [{ tag: "span.comment-icon-node" }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "span",
      mergeAttributes(HTMLAttributes, {
        class: "comment-icon-node",
        style:
          "cursor: pointer; margin-left: 4px; font-size: 14px; user-select: none;",
        title: "Click to view comment",
      }),
      "💬", // Visual icon inside the editor
    ];
  },
});

const ActionLink = Link.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      "data-action": { default: null },
      "data-images": { default: null },
    };
  },
});

// Letter Spacing Functionality

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    letterSpacing: {
      setLetterSpacing: (value: string) => ReturnType;
      unsetLetterSpacing: () => ReturnType;
    };
  }
}

const CustomTextStyle = TextStyle.extend({
  addAttributes() {
    return {
      ...this.parent?.(),

      letterSpacing: {
        default: null,

        parseHTML: (element) => {
          return element.style.letterSpacing || null;
        },

        renderHTML: (attributes) => {
          if (!attributes.letterSpacing) {
            return {};
          }

          return {
            style: `letter-spacing: ${attributes.letterSpacing}`,
          };
        },
      },
    };
  },

  addCommands() {
    return {
      setLetterSpacing:
        (value: string) =>
        ({ chain }: CommandProps) => {
          return chain()
            .setMark("textStyle", {
              letterSpacing: value,
            })
            .run();
        },

      unsetLetterSpacing:
        () =>
        ({ chain }: CommandProps) => {
          return chain()
            .setMark("textStyle", {
              letterSpacing: null,
            })
            .run();
        },
    };
  },
});

const CustomParagraph = Paragraph.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      textAlign: {
        default: null,
      },
      headingStyle: {
        default: null,
      },
      listNumber: {
        default: null,
      },
      lineHeight: {
        default: null,
        parseHTML: (element) => {
          return element.style.lineHeight || null;
        },
        renderHTML: (attributes) => {
          if (!attributes.lineHeight) {
            return {};
          }
          return {
            style: `line-height: ${attributes.lineHeight}`,
          };
        },
      },
      indent: {
        default: 0,

        parseHTML: (element) =>
          parseInt(element.getAttribute("data-indent") || "0", 10),

        renderHTML: (attributes) => {
          if (!attributes.indent) {
            return {};
          }

          return {
            "data-indent": attributes.indent,
            style: `margin-left: ${attributes.indent}px`,
          };
        },
      },
    };
  },
});

// ─────────────────────────────────────────────────────────────
// 3. SINGLE PAGE TIPTAP CONTAINER
// ─────────────────────────────────────────────────────────────

const EDITOR_EXTENSIONS = [
  StarterKit,
  CommentIconExtension,
  ActionLink.configure({ openOnClick: false }),
  Dropcursor,
  CustomParagraph,
  Underline,
  Subscript,
  Highlight.configure({ multicolor: true }),
  Table.configure({ resizable: true }),
  TableRow,
  CustomTableCell,
  CustomTableHeader,
  ResizableImage.configure({ allowBase64: true }),
  LSDBButton,
  CustomHeader,
  CustomFooter,
  PageNumberExtension,
  TotalPagesExtension,
  CustomTextStyle,
  FontFamily,
  FontSize,
  LineHeight,
  Color,
  TextAlign.configure({ types: ["heading", "paragraph", "header"] }),
];

function SinglePageEditor({
  contentHtml,
  maxHeightPx,
  onChange,
  onFocus,
  onUserEdit,
  onOverflow,
}: SinglePageEditorProps) {
  const checkOverflowRef = useRef<() => void>(() => {});
  // Tracks the last HTML this editor instance itself emitted via onUpdate
  // (e.g. typing, toolbar attribute changes like image resize/alignment).
  // The parent stores that HTML in state and passes it straight back down
  // as the `contentHtml` prop, which would otherwise make the effect below
  // think "external" content arrived and call editor.commands.setContent()
  // again — a full document replace that always collapses NodeSelections
  // (e.g. a selected/resizable image) back to a plain text selection, even
  // when the content is byte-for-byte identical. Skipping setContent for
  // self-originated echoes breaks that feedback loop while still letting
  // genuinely external content changes (initial load, overflow pushed in
  // from another page) sync normally.
  const lastEmittedHtmlRef = useRef<string | null>(null);

  const editor = useEditor({
    extensions: EDITOR_EXTENSIONS,
    content: contentHtml,

    editorProps: {
      handleDOMEvents: {
        // Tracks pointer movement between mousedown and click so we can
        // tell a genuine click apart from a drag-to-select. Used below by
        // handleClick to fix the blue/blur overlay bug.
        mousedown: (view, event) => {
          (view as any)._lastMouseDownAt = {
            x: (event as MouseEvent).clientX,
            y: (event as MouseEvent).clientY,
          };
          return false;
        },
        dblclick: (view, event) => {
          const target = event.target as HTMLElement;
          const paragraphEl = target.closest("p, div, li, span") as HTMLElement | null;

          if (paragraphEl && view.dom.contains(paragraphEl)) {
            if (scrollFromTocLine(paragraphEl)) {
              event.preventDefault();
              event.stopPropagation();
              return true;
            }
          }
          return false;
        },
        click: (view, event) => {
          const target = event.target as HTMLElement;

          // 1. Comment icon handler (unchanged)
          const commentIcon = target.closest(".comment-icon-node");
          if (commentIcon) {
            event.preventDefault();
            event.stopPropagation();

            const commentId = commentIcon.getAttribute("data-comment-id");

            window.dispatchEvent(
              new CustomEvent("open-comment-drawer", {
                detail: { commentId },
              }),
            );
            return true;
          }

          const linkElement = target.closest("a");

          // 2. Image drawer trigger link (unchanged)
          if (
            linkElement &&
            linkElement.getAttribute("data-action") === "open-image-drawer"
          ) {
            event.preventDefault();
            event.stopPropagation(); // Prevents TipTap from stealing focus

            // Force the parent to recognize THIS page's editor as the active one
            onFocusRef.current(editor);

            const imagesStr = linkElement.getAttribute("data-images");

            if (imagesStr) {
              try {
                const images = JSON.parse(imagesStr);

                // Exact position of the clicked link
                const pos = view.posAtDOM(linkElement, 0);

                // Find where the parent table ends so images can be appended later
                let tableEndPos = pos;
                try {
                  const resolvedPos = view.state.doc.resolve(pos);
                  for (let d = resolvedPos.depth; d > 0; d--) {
                    if (resolvedPos.node(d).type.name === "table") {
                      tableEndPos = resolvedPos.end(d) + 1;
                      break;
                    }
                  }

                  // Skip past previously inserted image tables so they stack sequentially
                  let nextNode = view.state.doc.nodeAt(tableEndPos);
                  while (nextNode) {
                    if (
                      nextNode.type.name === "paragraph" &&
                      nextNode.textContent.trim() === ""
                    ) {
                      const tempNext = view.state.doc.nodeAt(
                        tableEndPos + nextNode.nodeSize,
                      );
                      if (
                        tempNext &&
                        tempNext.type.name === "table" &&
                        tempNext.textContent.includes("Image:")
                      ) {
                        tableEndPos += nextNode.nodeSize + tempNext.nodeSize;
                        nextNode = view.state.doc.nodeAt(tableEndPos);
                        continue;
                      }
                    } else if (
                      nextNode.type.name === "table" &&
                      nextNode.textContent.includes("Image:")
                    ) {
                      tableEndPos += nextNode.nodeSize;
                      nextNode = view.state.doc.nodeAt(tableEndPos);
                      continue;
                    }
                    break;
                  }
                } catch (e) {
                  console.warn("Could not resolve table end", e);
                }

                // Put the cursor exactly on the link
                editor.commands.setTextSelection(pos);

                window.dispatchEvent(
                  new CustomEvent("open-image-drawer", {
                    detail: {
                      is_image: true,
                      images: images,
                      insertPosition: { type: "finding_image", tableEndPos },
                      targetEditor: editor,
                    },
                  }),
                );
              } catch (e) {
                console.error("Failed to parse drawer images", e);
              }
            }
            return true;
          }

          // // 3. NEW: Table of Contents / anchor navigation
          // if (linkElement && view.dom.contains(linkElement)) {
          //   const href = linkElement.getAttribute("href");

          //   if (href && href.startsWith("#") && href.length > 1) {
          //     event.preventDefault();
          //     event.stopPropagation();

          //     scrollToHeading(decodeURIComponent(href.slice(1)));
          //     return true;
          //   }
          // }

          // // 4. NEW: click on a TOC line written into the document body
          // if (!linkElement) {
          //   const paragraphEl = target.closest("p") as HTMLElement | null;

          //   if (paragraphEl && view.dom.contains(paragraphEl)) {
          //     if (scrollFromTocLine(paragraphEl)) {
          //       event.preventDefault();
          //       return true;
          //     }
          //   }
          // }

          return false;
        },
      },
      handleClick(view, pos, event) {
        if (event.shiftKey || event.altKey || event.ctrlKey || event.metaKey) {
          return false;
        }

        const start = (view as any)._lastMouseDownAt;
        const moved =
          !start ||
          Math.abs(start.x - event.clientX) > 4 ||
          Math.abs(start.y - event.clientY) > 4;

        if (!moved && view.state.selection instanceof CellSelection) {
          const $pos = view.state.doc.resolve(pos);
          const tr = view.state.tr.setSelection(TextSelection.near($pos));
          view.dispatch(tr);
          return true;
        }

        return false;
      },
    },
    onUpdate: ({ editor, transaction }) => {
      const html = editor.getHTML();
      lastEmittedHtmlRef.current = html;
      onChange(html);

      if (transaction.docChanged && !editor.isCapturingTransaction) {
        onUserEdit();
      }

      requestAnimationFrame(() => {
        setTimeout(() => checkOverflowRef.current(), 50);
      });
    },

    onFocus: ({ editor }) => {
      onFocus(editor);
    },
  });

  useEffect(() => {
    const handleRemoveComment = (e: any) => {
      const { commentId, anchorText } = e.detail || {};
      if (!editor || editor.isDestroyed) return;

      const { state, view } = editor;
      const tr = state.tr;
      const highlightMark = state.schema.marks.highlight;
      let isModified = false;

      const iconsToDelete: number[] = [];
      const highlightsToRemove: { from: number; to: number }[] = [];

      // Scan the document nodes
      state.doc.descendants((node: any, pos: number) => {
        // Strategy A: Find the icon exactly by its data-comment-id
        if (node.type.name === "commentIcon") {
          const nodeId = node.attrs?.["data-comment-id"];
          if (String(nodeId) === String(commentId)) {
            if (!iconsToDelete.includes(pos)) iconsToDelete.push(pos);

            // Queue the whole block for highlight removal
            const $pos = state.doc.resolve(pos);
            highlightsToRemove.push({ from: $pos.start(), to: $pos.end() });
          }
        }

        // Strategy B: Fallback - Find by the exact anchor text
        if (anchorText && anchorText.trim().length > 0) {
          const cleanAnchor = anchorText.trim();

          if (node.isText && node.text && node.text.includes(cleanAnchor)) {
            // Check if this matching text actually has a highlight on it
            if (node.marks.some((m: any) => m.type.name === "highlight")) {
              const start = pos + node.text.indexOf(cleanAnchor);
              const end = start + cleanAnchor.length;
              highlightsToRemove.push({ from: start, to: end });

              // Look ahead to see if there is an orphaned icon right after this text
              const blockEnd = state.doc.resolve(pos).end();
              state.doc.nodesBetween(end, blockEnd, (n, p) => {
                if (n.type.name === "commentIcon") {
                  if (!iconsToDelete.includes(p)) iconsToDelete.push(p);
                }
              });
            }
          }
        }
      });

      // 1. Remove Highlights
      if (highlightMark) {
        highlightsToRemove.forEach((range) => {
          tr.removeMark(range.from, range.to, highlightMark);
          isModified = true;
        });
      }

      // 2. Delete Icons (Iterate backwards so positions don't shift during deletion)
      iconsToDelete
        .sort((a, b) => b - a)
        .forEach((pos) => {
          tr.delete(pos, pos + 1);
          isModified = true;
        });

      // 3. Dispatch the changes and trigger an auto-save update
      if (isModified) {
        view.dispatch(tr);
        onChange(editor.getHTML());
        onUserEdit();
      }
    };

    window.addEventListener("remove-comment-from-editor", handleRemoveComment);
    return () => {
      window.removeEventListener(
        "remove-comment-from-editor",
        handleRemoveComment,
      );
    };
  }, [editor, onChange, onUserEdit]);

  const checkOverflowAndSplit = useCallback(() => {
    if (!editor || editor.isDestroyed) return;

    const dom = editor.view.dom as HTMLElement;
    const doc = editor.state.doc;

    // 0. A document's own "Table of Contents" block (title + entries) must
    // always end its own page. If this page contains a complete TOC block
    // followed by any other content, force a break right after the block
    // — even when the page still has room left — so nothing (e.g. a
    // Summary section) ever ends up sharing the TOC's page. Separately,
    // fixed section headings (e.g. "Report Details", "Table of Contents")
    // must always *start* a fresh page too, regardless of how much room
    // is left on the current one.
    let forcedSplitIndex: number | null = null;
    if (doc.childCount > 1) {
      const childTexts: string[] = [];
      for (let k = 0; k < doc.childCount; k++) {
        childTexts.push(doc.child(k).textContent || "");
      }
      const tocRangeWithTrailingContent = findTocBlockRanges(childTexts).find(
        (r) => r.end < doc.childCount - 1,
      );
      if (tocRangeWithTrailingContent) {
        forcedSplitIndex = tocRangeWithTrailingContent.end + 1;
      }

      for (let k = 1; k < childTexts.length; k++) {
        if (isForcedPageStartText(childTexts[k])) {
          forcedSplitIndex =
            forcedSplitIndex === null ? k : Math.min(forcedSplitIndex, k);
          break;
        }
      }
    }

    // 1. Define tolerance threshold
    const OVERFLOW_TOLERANCE = 15;
    const targetMaxHeight = maxHeightPx + OVERFLOW_TOLERANCE;

    // 2. Early return only if content safely fits AND no TOC block needs
    // to be forcibly separated from trailing content on this page.
    if (dom.scrollHeight <= targetMaxHeight && forcedSplitIndex === null) {
      return;
    }

    const children = Array.from(dom.children) as HTMLElement[];
    if (children.length === 0) return;

    const containerTop = dom.getBoundingClientRect().top;

    // NOTE: Tables are never split by row here. A table (with any LSDB
    // button immediately before it) is always moved to the next page as a
    // single, intact unit if it doesn't fit the remaining space — never
    // cloned/divided into multiple separate table nodes. This preserves
    // the original table's structure (rows/columns/formatting/data) for
    // editing, reopening, and PDF generation alike, even at the cost of
    // some unused space at the bottom of the page it didn't fit on.

    // If there's only 1 root block on the page, we cannot split it further vertically
    if (doc.childCount <= 1) return;

    let splitIndex = children.length;

    // 3. Find the exact child element that crosses the boundary (only
    // meaningful when the page is actually overflowing).
    if (dom.scrollHeight > targetMaxHeight) {
      for (let i = 0; i < children.length; i++) {
        const childBottom =
          children[i].getBoundingClientRect().bottom - containerTop;

        // Check against the same threshold as the initial check
        if (childBottom > targetMaxHeight) {
          splitIndex = i;
          break;
        }
      }
    }

    // Whichever constraint requires the earlier cut wins: natural overflow
    // or the forced TOC/trailing-content boundary from step 0.
    if (forcedSplitIndex !== null && forcedSplitIndex < splitIndex) {
      splitIndex = forcedSplitIndex;
    }

    // 4. Handle edge cases (Keep LSDB button attached to its table)
    if (splitIndex > 1 && splitIndex < doc.childCount) {
      const nodeToMove = doc.child(splitIndex);
      const previousNode = doc.child(splitIndex - 1);

      if (
        nodeToMove.type.name === "table" &&
        previousNode.type.name === "lsdbButton"
      ) {
        splitIndex -= 1;
      }
    }

    // 4b. Keep a document's own "Table of Contents" block (title + entries)
    // intact — never let it straddle a page boundary. If the computed
    // split point falls inside such a block, push the whole block to the
    // next page instead, the same way a table is moved whole rather than
    // split by row. (Skipped when the block starts at the very first
    // child, since moving it would empty the page entirely with nothing
    // gained — that pathological case is left to the normal split logic.)
    if (splitIndex > 0 && splitIndex < doc.childCount) {
      const childTexts: string[] = [];
      for (let k = 0; k < doc.childCount; k++) {
        childTexts.push(doc.child(k).textContent || "");
      }
      const tocRange = findTocBlockRanges(childTexts).find(
        (r) => splitIndex > r.start && splitIndex <= r.end,
      );
      if (tocRange && tocRange.start > 0) {
        splitIndex = tocRange.start;
      }
    }

    // 5. CRITICAL FIX: If the VERY FIRST element exceeds the height,
    // do NOT split immediately unless there are at least 2 children fitting before it.
    // Move starting from index 1 (keep at least 1 element on the current page).
    if (splitIndex === 0) {
      splitIndex = 1;
    }

    // Safety check: Don't perform a zero-length cut or split beyond the doc bounds
    if (splitIndex >= doc.childCount) return;

    // 6. Calculate document positions for range slicing
    let fromPos = 0;
    for (let i = 0; i < splitIndex; i++) {
      fromPos += doc.child(i).nodeSize;
    }

    // Ensure position doesn't exceed document boundary
    const toPos = doc.content.size;
    if (fromPos >= toPos) return;

    // 7. Extract overflow fragment & update TipTap state
    const overflowFragment = doc.slice(fromPos, toPos).content.toJSON() ?? [];
    const overflowHtml = generateHTML(
      { type: "doc", content: overflowFragment },
      EDITOR_EXTENSIONS,
    );

    // Delete split content from current editor and notify parent handler
    editor.chain().deleteRange({ from: fromPos, to: toPos }).run();
    onOverflow(overflowHtml);
  }, [editor, maxHeightPx, onOverflow]);

  useEffect(() => {
    checkOverflowRef.current = checkOverflowAndSplit;
  }, [checkOverflowAndSplit]);

  const onFocusRef = useRef(onFocus);
  useEffect(() => {
    onFocusRef.current = onFocus;
  }, [onFocus]);

  useEffect(() => {
    if (editor) {
      onFocusRef.current(editor);
    }
  }, [editor]);

  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    if (contentHtml === lastEmittedHtmlRef.current) return;
    if (contentHtml !== editor.getHTML()) {
      queueMicrotask(() => {
        if (!editor.isDestroyed) {
          editor
            .chain()
            .setMeta("addToHistory", false) // <-- external syncs are not undoable
            .setContent(contentHtml, { emitUpdate: false })
            .run();
          lastEmittedHtmlRef.current = null;
          requestAnimationFrame(() => checkOverflowRef.current());
        }
      });
    }
  }, [contentHtml, editor]);

  useEffect(() => {
    if (!editor) return;
    const dom = editor.view.dom as HTMLElement;
    const observer = new ResizeObserver(() => {
      requestAnimationFrame(() => checkOverflowRef.current());
    });
    observer.observe(dom);

    return () => observer.disconnect();
  }, [editor]);

  if (!editor) return null;

  return <EditorContent editor={editor} />;
}

// The JSON-to-HTML pagination engine (renderSingleElementToHtml,
// transformJsonToPageHtmlChunks, etc.) lives in its own dependency-free
// module so it can be unit tested without pulling in React/@tiptap. See
// ./reportPageGenerator.ts.

// ─────────────────────────────────────────────────────────────
// 5. MAIN COMPONENT
// ─────────────────────────────────────────────────────────────

export default function TipTap() {
  const [zoom, setZoom] = useState(100);
  const [darkMode, setDarkMode] = useState(false);
  const [pageHtmlChunks, setPageHtmlChunks] = useState<string[]>([]);
  const [isDocumentLoading, setIsDocumentLoading] = useState(true);
  const [docHeaderHtml, setDocHeaderHtml] = useState<string>(""); // <--- ADD THIS
  const [docFooterHtml, setDocFooterHtml] = useState<string>("");
  const [activeEditor, setActiveEditor] = useState<any>(null);
  const [commentRange, setCommentRange] = useState<{
    from: number;
    to: number;
  } | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [commentType, setCommentType] = useState<CommentType>("text");
  const [selectedText, setSelectedText] = useState("");
  const [commentText, setCommentText] = useState("");
  const [showCommentBox, setShowCommentBox] = useState(false);
  const [showCommentsPanel, setShowCommentsPanel] = useState(false);
  const [activeCommentId, setActiveCommentId] = useState<string | null>(null);
  const [showDeletePageDialog, setShowDeletePageDialog] = useState(false);
  const [deletePageIndex, setDeletePageIndex] = useState<number | null>(null);
  const [deletePageNumber, setDeletePageNumber] = useState<number | null>(null);
  const [currentPageNumber, setCurrentPageNumber] = useState(1);

  const [isDirty, setIsDirty] = useState(false);
  const lastSavedContent = useRef<string>("");
  const isContentSet = useRef(false);

  const { id } = useParams<{ id: string }>();
  const reportID = Number(id);
  const c = useStyles();

  const history = useHistory();
  const location = useLocation();
  // const mode = new URLSearchParams(location.search).get("mode"); // "new" | "continue"

  const [showFindReplace, setShowFindReplace] = useState(false);
  const [findText, setFindText] = useState("");
  const [replaceText, setReplaceText] = useState("");

  // NAVIGATION PANE: lets users jump between report sections (headings).
  const [showNavPane, setShowNavPane] = useState(false);
  const [navHeadings, setNavHeadings] = useState<
    {
      pageIndex: number;
      indexWithinPage: number;
      level: number;
      text: string;
    }[]
  >([]);

  const handleNavHeadingClick = useCallback(
    (heading: { pageIndex: number; indexWithinPage: number }) => {
      const pageContentEls = document.querySelectorAll(
        ".docgen-page-content .ProseMirror",
      );
      const targetPage = pageContentEls[heading.pageIndex] as
        HTMLElement | undefined;
      if (!targetPage) return;
      const headingEls = targetPage.querySelectorAll("h1, h2, h3");
      const targetHeading = headingEls[heading.indexWithinPage] as
        HTMLElement | undefined;
      targetHeading?.scrollIntoView({ behavior: "smooth", block: "start" });
      setShowNavPane(false);
    },
    [],
  );

  // NEW: State to track exact TipTap Node positions for highlighting
  const [matches, setMatches] = useState<{ from: number; to: number }[]>([]);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);

  // Monitor text changes in the editor to re-calculate matches instantly

  // Helper function to extract matches synchronously from the editor document
  const getMatches = (doc: any, findText: string) => {
    if (!findText.trim()) return [];

    const searchText = findText.trim();
    const escapedFindText = searchText.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(escapedFindText, "gi");
    const newMatches: { from: number; to: number }[] = [];

    doc.descendants((node: any, pos: number) => {
      if (!node.isText || !node.text) return;

      let match: RegExpExecArray | null;
      while ((match = regex.exec(node.text)) !== null) {
        newMatches.push({
          from: pos + match.index,
          to: pos + match.index + match[0].length,
        });

        if (match[0].length === 0) {
          regex.lastIndex++;
        }
      }
    });

    return newMatches;
  };

  // ... inside your component:

  useEffect(() => {
    if (!activeEditor) return;

    const updateMatches = () => {
      const newMatches = getMatches(activeEditor.state.doc, findText);
      setMatches(newMatches);

      setCurrentMatchIndex((previousIndex) => {
        if (newMatches.length === 0) return 0;
        return previousIndex >= newMatches.length ? 0 : previousIndex;
      });
    };

    updateMatches();

    activeEditor.on("update", updateMatches);
    return () => {
      activeEditor.off("update", updateMatches);
    };
  }, [findText, activeEditor]);

  const handleNextMatch = () => {
    if (matches.length === 0 || !activeEditor) return;

    const nextIdx = (currentMatchIndex + 1) % matches.length;
    setCurrentMatchIndex(nextIdx);

    activeEditor
      .chain()
      .focus()
      .setTextSelection({
        from: matches[nextIdx].from,
        to: matches[nextIdx].to,
      })
      .scrollIntoView()
      .run();
  };

  const handlePrevMatch = () => {
    if (matches.length === 0 || !activeEditor) return;

    const prevIdx = (currentMatchIndex - 1 + matches.length) % matches.length;
    setCurrentMatchIndex(prevIdx);

    activeEditor
      .chain()
      .focus()
      .setTextSelection({
        from: matches[prevIdx].from,
        to: matches[prevIdx].to,
      })
      .scrollIntoView()
      .run();
  };

  const handleExecuteFindReplace = (replaceSingle: boolean = false) => {
    if (!findText || matches.length === 0 || !activeEditor) {
      return;
    }

    if (replaceSingle) {
      const currentMatch = matches[currentMatchIndex];
      if (!currentMatch) return;

      // 1. Perform the single replacement
      activeEditor
        .chain()
        .focus()
        .insertContentAt(
          {
            from: currentMatch.from,
            to: currentMatch.to,
          },
          replaceText,
        )
        .run();

      // 2. Immediately scan the updated document synchronously
      const updatedMatches = getMatches(activeEditor.state.doc, findText);
      setMatches(updatedMatches);

      // 3. Highlight and move selection to the next match
      if (updatedMatches.length > 0) {
        const nextIdx =
          currentMatchIndex >= updatedMatches.length ? 0 : currentMatchIndex;

        setCurrentMatchIndex(nextIdx);

        activeEditor
          .chain()
          .focus()
          .setTextSelection({
            from: updatedMatches[nextIdx].from,
            to: updatedMatches[nextIdx].to,
          })
          .scrollIntoView()
          .run();
      } else {
        setCurrentMatchIndex(0);
      }

      return;
    }

    // Replace All (from bottom to top)
    const reversedMatches = [...matches].reverse();
    let chain = activeEditor.chain().focus();

    reversedMatches.forEach((match) => {
      chain = chain.insertContentAt(
        {
          from: match.from,
          to: match.to,
        },
        replaceText,
      );
    });

    chain.run();
  };

  const SHOW_PANEL_STATUSES: StatusKey[] = [
    "review",
    "corr_rev",
    "corr_ver",
    "approval",
    "corr_app",
  ];
  const query = new URLSearchParams(location.search);

  const mode = query.get("mode");
  const role = query.get("role") as Role;

  const statusParam = query.get("status") as StatusKey | null;
  const urlParam = query.get("url_param") as StatusKey;

  // const createurl = "https://lsdbhaveblueuat.azurewebsites.net/api/1.0/docgen/report-template/?report_result_id=23"

  const isReviewer = role === "reviewer";

  const {
    error: errorchecklistData,
    data: checklistData,
    isLoading: isLoadingchecklistData,
    isError: isErrorchecklistData,
    refetch: refetchChecklist,
  } = useAvailableCheckList(String(reportID), {
    enabled: isReviewer && !!reportID,
  });

  const isWriter = role === "writer";

  const {
    data: pendingCheckList,
    isLoading: isLoadingPendingCheckList,
    isError: isErrorPendingCheckList,
    refetch: refetchPendingCheckList,
  } = usePendingCheckList(String(reportID), {
    enabled: isWriter && statusParam === "corr_rev" && !!reportID,
  });

  const is_writer =
    role === "writer" &&
    (statusParam === "corr_ver" || statusParam === "corr_app");

  const is_verifier = role === "verifier" && statusParam === "verify";
  const is_approver = role === "approver" && statusParam === "approval";

  const shouldGetVerifierComments = is_writer || is_verifier || is_approver;

  const {
    error: errorverifierData,
    data: verifierData,
    isLoading: isLoadingverifierData,
    isError: isverifierData,
    refetch: refetchVerifierComments,
  } = useGetVerifierComments(String(reportID), is_writer, {
    enabled: shouldGetVerifierComments && !!reportID,
  });

  const refreshDrawerApis = () => {
    if (!reportID) return;

    if (isReviewer) {
      refetchChecklist();
    }

    if (isWriter && statusParam === "corr_rev") {
      refetchPendingCheckList();
    }

    if (shouldGetVerifierComments) {
      refetchVerifierComments();
    }
  };

  const showSidePanel = statusParam
    ? SHOW_PANEL_STATUSES.includes(statusParam)
    : false;

  const [reportData, setReportData] = useState<any>(null);
  const [reportVersion, setReportVersion] = useState<any>(null);
  const [reportNumber, setReportNumber] = useState<any>("");
  const [version_history, setVersionHistory] = useState<any>(null);
  const [workOrder, setWorkOrder] = useState<any>(null);
  const [ProjectId, setProjectId] = useState<any>(null);
  const [isNewReport, setIsNewReport] = useState(mode === "new");
  const [showSidePanelDrawer, setShowSidePanelDrawer] = useState(false);

  const { authAxios } = useFetchContext();
  const { mutateAsync: ManualTipTapSave } = useTipTapManualSave();
  const { mutateAsync: createReport } = useCreateReport();
  const { mutateAsync: continueReport } = useContinueReportWriting();
  const { mutateAsync: AddComments } = useWriterAddComments();
  const { mutateAsync: DocuCommentsdelete } = useDeleteDocComments();

  useEffect(() => {
    if (showSidePanelDrawer || showCommentsPanel) {
      refreshDrawerApis();
    }
  }, [showSidePanelDrawer, showCommentsPanel]);

  const {
    mutateAsync: movewritertoReviewer,
    isLoading: isMovingWriterToReviewer,
  } = useMoveReport();
  const { data } = useReportIntakeImages(String(reportID));
  const ctl = useAgendaState();
  const { handleTransition } = useReportTransitions(ctl);

  const openRow = useMemo(() => {
    const existing = ctl.state.rows.find((r) => r.id === reportID);
    if (!existing) return null;
    return { ...existing, st: statusParam ?? existing.st };
  }, [ctl.state.rows, reportID, statusParam]);

  const sidePanelBadgeCount = useMemo(() => {
    if (!showSidePanel || !openRow) return 0;
    switch (openRow.st) {
      case "review":
        return ctl.ckPending.length;
      case "corr_rev":
        return ctl.ckPending.length - ctl.ckAcked.length;
      case "verify":
        return ctl.cmHeld("verify").length;
      case "corr_ver":
        return ctl.cmSent("verify").length - ctl.cmAck("verify").length;
      case "approval":
        return ctl.cmHeld("approval").length;
      case "corr_app":
        return ctl.cmSent("approval").length - ctl.cmAck("approval").length;
      default:
        return 0;
    }
  }, [showSidePanel, openRow, ctl]);

  const initialLoadDone = useRef(false);
  useEffect(() => {
    if (!reportID || initialLoadDone.current) return;
    initialLoadDone.current = true;

    const loadInitialData = async () => {
      try {
        const result =
          mode === "new"
            ? await createReport({
                report_result_id: Number(reportID),
                template: urlParam,
              })
            : await continueReport({
                reportId: Number(reportID),
                template: urlParam,
              });
        setReportData(result);
        setReportVersion(result?.latest_version);
        setReportNumber(result?.report_number || "");
        setVersionHistory(result?.version_history);
        setWorkOrder(result?.work_order);
        setProjectId(result?.project_number);
        setIsNewReport(mode === "new");

        // Prevent a reload from re-fetching the blank template and wiping
        // whatever's been saved since — once we've loaded as "new", the URL
        // itself becomes "continue" for the rest of this session.
        // if (mode === "new") {
        //   history.replace(
        //     `/reports/report_writers_agenda/tiptap/${reportID}?mode=continue`,
        //   );
        // }
      } catch (err: any) {
        toast.error(
          err?.response?.data?.error || "Failed to load report template",
        );
        setIsDocumentLoading(false);
      }
    };

    loadInitialData();
    // Deliberately reportID-only: this must run exactly once per report,
    // and must NOT re-fire when history.replace changes `mode` above.
  }, [reportID]);

  const toggleTheme = () => setDarkMode((prev) => !prev);
  const reportImages = data?.image_path ?? [];
  const [selectedImages, setSelectedImages] = useState<any[]>([]);

  const defaultChromeConfig: ChromeConfig = useMemo(() => {
    const elements = reportData?.elements || reportData?.data?.elements || [];

    const configElement =
      elements.find((el: any) => el.type === "editor_props") ||
      elements.find((el: any) => el.type === "chrome");

    const jsonConfig = configElement?.data;

    return {
      header: {
        label:
          jsonConfig?.header?.label ||
          reportData?.document_name ||
          "Intake Report",
        logoDataUri: jsonConfig?.header?.logoDataUri || undefined,
        separatorColor: jsonConfig?.header?.separatorColor || "#095ae8",
      },
      footer: {
        left: jsonConfig?.footer?.left || `Work Order: ${workOrder || "N/A"}`,
        center: jsonConfig?.footer?.center || `R${ProjectId}`,
        separatorColor: jsonConfig?.footer?.separatorColor || "#095ae8",
        showPageNumbers: jsonConfig?.footer?.showPageNumbers ?? true,
      },
      pageSetup: {
        widthInches: jsonConfig?.pageSetup?.widthInches ?? 8.27,
        heightInches: jsonConfig?.pageSetup?.heightInches ?? 11.69,
        marginTop: jsonConfig?.pageSetup?.marginTop ?? 0.8,
        marginBottom: jsonConfig?.pageSetup?.marginBottom ?? 0.8,
        marginLeft: 0.8,
        marginRight: 0.8,
      },
      differentFirstPage: jsonConfig?.differentFirstPage ?? true,
    };
  }, [reportData, workOrder, ProjectId]);

  const contentHeightPx = useMemo(() => {
    const { heightInches } = defaultChromeConfig.pageSetup;
    const pageHeightPx = heightInches * 96; // 11.69 * 96 = ~1122px

    // NOTE: pageSetup.marginTop/marginBottom are NOT rendered as real
    // top/bottom spacing anywhere in PageChrome (the content area only ever
    // uses a fixed 20px vertical padding; see ".docgen-page-content" below).
    // Previously this budget also subtracted marginTop/marginBottom on top
    // of the header/footer/padding heights, which reserved space that was
    // never actually consumed. Because ".docgen-page" is stretched to a
    // fixed minHeight (pageHeightPx) and the footer is pinned to the bottom
    // via `marginTop: "auto"`, that unclaimed space was pushed into the gap
    // between the content and the footer — showing up as a large blank area
    // near the bottom of every page (and forcing content to paginate onto
    // more pages than necessary). Header/Footer heights already account for
    // their own vertical space, so marginTop/marginBottom must not be
    // subtracted again here.
    // Header and Footer visual heights + padding (approx 40px - 60px each)
    const HEADER_HEIGHT_PX = docHeaderHtml ? 85 : 50;
    const FOOTER_HEIGHT_PX = docFooterHtml ? 55 : 40;
    const CONTENT_VERTICAL_PADDING = 40; // 20px top + 20px bottom padding

    const availableHeight =
      pageHeightPx -
      HEADER_HEIGHT_PX -
      FOOTER_HEIGHT_PX -
      CONTENT_VERTICAL_PADDING;

    return Math.max(availableHeight, 400); // Ensure a sensible baseline limit
  }, [defaultChromeConfig, docHeaderHtml, docFooterHtml]); // Add dependencies here

  // The cover/first page doesn't render the header/footer chrome at all
  // (see PageChrome's `showChrome`), so it genuinely has more usable room
  // than `contentHeightPx` assumes (which reserves header/footer space for
  // every page uniformly). Without this, the cover page's own trailing
  // content (e.g. a logo right after the title block) gets bumped to page
  // 2 even though there's clearly unused space left on page 1.
  const firstPageContentHeightPx = useMemo(() => {
    if (!defaultChromeConfig.differentFirstPage) return contentHeightPx;
    const { heightInches } = defaultChromeConfig.pageSetup;
    const pageHeightPx = heightInches * 96;
    const CONTENT_VERTICAL_PADDING = 40;
    return Math.max(pageHeightPx - CONTENT_VERTICAL_PADDING, 400);
  }, [defaultChromeConfig, contentHeightPx]);

  const handleImageSelect = (image: any) => {
    setSelectedImages((prev) => {
      const exists = prev.some(
        (item) =>
          item.type === image.type && item.images[0] === image.images[0] &&
          item.stage === image.stage &&
          (!image.stage || item.serial_number === image.serial_number),
      );
      if (exists) {
        return prev.filter(
          (item) =>
            !(item.type === image.type && item.images[0] === image.images[0] &&
              item.stage === image.stage &&
              (!image.stage || item.serial_number === image.serial_number)),
        );
      }
      return [...prev, image];
    });
  };

  const base64ToBlobUrl = (base64: string) => {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const blob = new Blob([new Uint8Array(byteNumbers)], {
      type: "image/jpeg",
    });
    return URL.createObjectURL(blob);
  };

  const handleInsertImages = (insertPayload?: any, specificEditor?: any) => {
    // 1. Use the specific editor if provided, otherwise fallback to activeEditor
    const editorToUse = specificEditor || activeEditor;
    if (!editorToUse) return;

    if (insertPayload?.type === "fe_stage_images") {
      try {
        const position = insertPayload.tablePosition;
        const table = editorToUse.state.doc.nodeAt(position);
        if (table?.type.name !== "table") {
          throw new Error("The FE image table moved. Fetch its images again before inserting.");
        }
        const updatedTable = bindSelectedFeImages(
          table.toJSON(), insertPayload.records, selectedImages,
        );
        if (!updatedTable) throw new Error("No FE stage image rows found in the target table.");
        editorToUse.chain().focus().insertContentAt(
          { from: position, to: position + table.nodeSize }, updatedTable,
        ).run();
        setSelectedImages([]);
      } catch (error) {
        console.error("FE IMAGE BINDING ERROR:", error);
        alert(error instanceof Error ? error.message : "Unable to insert FE stage images.");
      }
      return;
    }

    let isFindingImage = false;
    let tableEndPos = null;

    // Detect if this payload came from our custom "Select Image" link
    if (insertPayload && insertPayload.type === "finding_image") {
      isFindingImage = true;
      tableEndPos = insertPayload.tableEndPos;
    }

    const content: any[] = [];
    const imageNames: string[] = [];

    selectedImages.forEach((item) => {
      item.images.forEach((img: any) => {
        let imageUrl = img;
        if (item.type === "base64") {
          imageUrl = base64ToBlobUrl(img);
        }

        // Extract the name from the payload
        const iName = item.serial_number || item.image_name || "Attached Below";
        imageNames.push(iName);

        if (isFindingImage) {
          // Format image as a TipTap Table to append below the main table
          content.push({
            type: "paragraph",
            content: [{ type: "text", text: " " }],
          });
          content.push({
            type: "table",
            content: [
              {
                type: "tableRow",
                content: [
                  {
                    type: "tableHeader",
                    attrs: { colspan: 1, rowspan: 1, colwidth: null },
                    content: [
                      {
                        type: "paragraph",
                        attrs: { textAlign: "center" },
                        content: [{ type: "text", text: `Image: ${iName}` }],
                      },
                    ],
                  },
                ],
              },
              {
                type: "tableRow",
                content: [
                  {
                    type: "tableCell",
                    attrs: { colspan: 1, rowspan: 1, colwidth: null },
                    content: [
                      {
                        type: "paragraph",
                        attrs: { textAlign: "center" }, // <-- Centers the image inside the table cell
                        content: [
                          {
                            type: "image",
                            attrs: {
                              src: imageUrl,
                              width: "200px", // <-- Resizable default width
                            },
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          });
        } else {
          // Standard fallback formatting (Centered and Resizable)
          content.push({
            type: "paragraph",
            attrs: { textAlign: "center" }, // <-- Centers the standard image
            content: [
              {
                type: "image",
                attrs: {
                  src: imageUrl,
                  width: "150px", // <-- Resizable default width
                },
              },
            ],
          });
        }
      });
    });

    if (content.length === 0) return; // Prevent inserting nothing

    if (isFindingImage && tableEndPos) {
      // Save the current cursor position (which is accurately on the "Select Image" link)
      const linkPos = editorToUse.state.selection.from;

      // 1. Insert the image table(s) below the main table
      editorToUse.chain().insertContentAt(tableEndPos, content).run();

      // 2. Restore the selection to the link, then replace the link text with the chosen image names
      editorToUse
        .chain()
        .focus()
        .setTextSelection(linkPos)
        .extendMarkRange("link") // Highlights the whole link
        .insertContent(imageNames.join(", ")) // Replaces link with image names
        .unsetLink() // Removes the blue hyperlink styling
        .run();
    } else {
      // Standard insertion logic
      const standardPos =
        typeof insertPayload === "number"
          ? insertPayload
          : editorToUse.state.selection.from;
      editorToUse.chain().focus().insertContentAt(standardPos, content).run();
    }

    // Clear selection after insert
    setSelectedImages([]);
  };

  const handleSubmit = async () => {
    try {
      const currentRole = role?.trim().toLowerCase() ?? "";
      const currentStatus = statusParam?.trim().toLowerCase() ?? "";

      if (
        currentRole !== "writer" ||
        !["writing", "notstarted"].includes(currentStatus)
      ) {
        toast.error("Report cannot be submitted in the current status.");
        return;
      }

      // Only writer can submit when status is writing or notstarted
      if (
        currentRole !== "writer" ||
        !["writing", "notstarted"].includes(currentStatus)
      ) {
        toast.error("Report cannot be submitted in the current status.");
        return;
      }

      const sent_from = "writer";
      const sent_to = "reviewer";

      const payload = {
        authAxios,
        report_result_id: reportID,
        sent_from,
        sent_to,
      };

      console.log("Submit payload:", payload);

      // 1. Save latest TipTap content first
      await ManualTipTapSave({
        authAxios,
        report_result_id: reportID,
        save_type: "manual",
        content: fullTipTapJSON,
      });

      await movewritertoReviewer(payload);

      toast.success("Writer submitted successfully to reviewer.");
      history.push("/reports/report_module");
    } catch (error: any) {
      console.error("Failed to submit report:", error);

      toast.error(error?.response?.data?.error || "Failed to submit report.");
    }
  };

  useEffect(() => {
    const handleOpenCommentDrawer = (e: any) => {
      setActiveCommentId(e.detail.commentId);
      setShowCommentsPanel(true);
    };

    window.addEventListener("open-comment-drawer", handleOpenCommentDrawer);
    return () =>
      window.removeEventListener(
        "open-comment-drawer",
        handleOpenCommentDrawer,
      );
  }, []);

  const getFullDocumentTipTapJSON = useCallback(() => {
    const fullBodyHtml = pageHtmlChunks.join("");
    // Reattach header and footer so they persist in the backend database
    const fullHtml = `${docHeaderHtml}${fullBodyHtml}${docFooterHtml}`;
    return generateJSON(fullHtml, EDITOR_EXTENSIONS);
  }, [pageHtmlChunks, docHeaderHtml, docFooterHtml]);

  useEffect(() => {
    if (isContentSet.current) return;

    if (!reportData) {
      setIsDocumentLoading(false);
      return;
    }

    // 1. Debugging check to see what the API actually gave us
    console.log("Raw API reportData received:", reportData);

    // 2. Unpack the data safely
    let rawData = reportData;

    // If the API sent a stringified JSON, parse it into an object
    if (typeof rawData === "string") {
      try {
        rawData = JSON.parse(rawData);
      } catch (e) {
        console.error("Failed to parse reportData string", e);
      }
    }

    // If Axios nested the response inside an extra "data" object, unwrap it
    if (
      rawData?.data &&
      (rawData.data.type === "doc" || rawData.data.elements)
    ) {
      rawData = rawData.data;
      if (typeof rawData === "string") {
        try {
          rawData = JSON.parse(rawData);
        } catch (e) {}
      }
    }

    console.log("Processed rawData ready for editor:", rawData);

    // DECLARE THE CHUNKS ARRAY HERE SO IT CAN BE ACCESSED
    let chunks: string[] = [];

    // 3. Render based on the detected format (Native TipTap vs Custom Schema)
    if (rawData?.type === "doc" && Array.isArray(rawData?.content)) {
      // SANITIZE THE JSON TO PREVENT "Empty text nodes" CRASH
      rawData = sanitizeTipTapJSON(rawData);
      rawData = normalizeImageSrcsInJson(rawData);

      // --- NEW LOGIC: EXTRACT HEADER AND FOOTER ---
      let currentHeaderHtml = "";
      let currentFooterHtml = "";

      // Create a copy of the nodes so we can safely remove the header/footer
      let bodyNodes = [...rawData.content];

      // 1. Find, generate HTML for, and remove the header
      const headerNodeIndex = bodyNodes.findIndex(
        (n: any) => n.type === "header",
      );
      if (headerNodeIndex !== -1) {
        currentHeaderHtml = generateHTML(
          { type: "doc", content: [bodyNodes[headerNodeIndex]] },
          EDITOR_EXTENSIONS,
        );
        bodyNodes.splice(headerNodeIndex, 1);
      }

      // 2. Find, generate HTML for, and remove the footer
      const footerNodeIndex = bodyNodes.findIndex(
        (n: any) => n.type === "footer",
      );
      if (footerNodeIndex !== -1) {
        currentFooterHtml = generateHTML(
          { type: "doc", content: [bodyNodes[footerNodeIndex]] },
          EDITOR_EXTENSIONS,
        );
        bodyNodes.splice(footerNodeIndex, 1);
      }

      // 3. Save extracted HTML to state so PageChrome can use them on every page
      setDocHeaderHtml(currentHeaderHtml);
      setDocFooterHtml(currentFooterHtml);
      // ----------------------------------------------

      // Generate HTML exclusively for the body (without header/footer)
      const fullHtml = generateHTML(
        { type: "doc", content: bodyNodes },
        EDITOR_EXTENSIONS,
      );
      const nodes = splitTopLevelNodes(fullHtml);

      // Pack the body's top-level nodes onto pages. This keeps a table (or
      // an <lsdb-button> + table cluster) intact rather than split by row,
      // and keeps a document's own "Table of Contents" block intact while
      // always forcing a page break right after it, so no other section
      // (e.g. a "Summary") ever shares the TOC's page. The first page gets
      // its own (larger) budget since its header/footer chrome is hidden.
      chunks = packNodesIntoPages(
        nodes,
        contentHeightPx,
        640,
        firstPageContentHeightPx,
      );
    } else {
      // Custom JSON format (relies on the elements array)
      chunks = transformJsonToPageHtmlChunks(rawData, contentHeightPx);
    }

    // Fallback if chunks are completely empty
    if (chunks.length === 0) {
      chunks = ["<p></p>"];
    }

    // The auto-inserted "Report Information" table (added in an earlier
    // iteration) has been removed per request — the cover page's own
    // per-report-type tables (see reportInfoFieldAliases.ts) already cover
    // this. If any previously-saved report still has that block baked into
    // its content, strip it out here so it stops appearing everywhere.
    if (chunks[0]?.includes(REPORT_INFO_TABLE_MARKER)) {
      const container = document.createElement("div");
      container.innerHTML = chunks[0];
      container
        .querySelectorAll(`.${REPORT_INFO_TABLE_MARKER}`)
        .forEach((el) => el.remove());
      chunks[0] = container.innerHTML;
    }

    setPageHtmlChunks(chunks);
    isContentSet.current = true;

    queueMicrotask(() => {
      const fullHtml = chunks.join("");
      const initialTipTapJSON = generateJSON(fullHtml, EDITOR_EXTENSIONS);

      lastSavedContent.current = JSON.stringify(initialTipTapJSON);
      setIsDirty(false);
      setIsDocumentLoading(false);
    });
  }, [reportData, contentHeightPx, docHeaderHtml, docFooterHtml]);

  // Recompute the section navigation list whenever the paginated content
  // changes, by parsing each page's HTML for heading tags.
  useEffect(() => {
    const parser = new DOMParser();
    const allHeadings: typeof navHeadings = [];

    pageHtmlChunks.forEach((html, pageIndex) => {
      const parsedDoc = parser.parseFromString(html || "", "text/html");
      const headingEls = Array.from(parsedDoc.querySelectorAll("h1, h2, h3"));
      headingEls.forEach((el, indexWithinPage) => {
        const text = el.textContent?.trim();
        if (!text) return;
        allHeadings.push({
          pageIndex,
          indexWithinPage,
          level: Number(el.tagName.substring(1)),
          text,
        });
      });
    });

    setNavHeadings(allHeadings);
  }, [pageHtmlChunks]);

  const handlePageChunkChange = (index: number, newHtml: string) => {
    setPageHtmlChunks((prev) => {
      const updated = [...prev];
      updated[index] = newHtml;

      // Automatically remove the page when the user empties it.
      if (updated.length > 1 && isEffectivelyEmpty(newHtml)) {
        updated.splice(index, 1);
      }

      return updated;
    });
  };

  const handlePageOverflow = useCallback(
    (pageIndex: number, overflowHtml: string) => {
      if (!overflowHtml) return;
      setPageHtmlChunks((prev) => {
        const updated = [...prev];
        if (pageIndex === updated.length - 1) {
          updated.push(overflowHtml);
        } else {
          updated[pageIndex + 1] = overflowHtml + updated[pageIndex + 1];
        }
        return updated;
      });
      setIsDirty(true);
    },
    [],
  );

  const handleEditorFocus = useCallback((editorInstance: any) => {
    setActiveEditor((prev: any) => {
      if (prev === editorInstance) return prev;
      return editorInstance;
    });
  }, []);

  const fullTipTapJSON = useMemo(
    () => getFullDocumentTipTapJSON(),
    [getFullDocumentTipTapJSON],
  );
  const tocItems = useMemo(
    () => extractTOCItems(fullTipTapJSON),
    [fullTipTapJSON],
  );

  // Handles a click on an entry extracted from the document's own Table of
  // Contents. Works for any report type since it relies solely on the
  // heading/TOC text mapping produced by extractTOCItems, not on hardcoded
  // section names.
  const handleTocItemClick = useCallback(
    (item: TOCItem) => {
      const key = normalizeTitle(item.title);
      const occurrence = tocItems
        .slice(0, item.itemIndex)
        .filter((i) => normalizeTitle(i.title) === key).length;

      const found =
        (item.headingId && scrollToHeading(item.headingId)) ||
        scrollToHeadingByTitle(item.title, occurrence);

      if (!found) {
        console.warn(`Could not find heading for: "${item.title}"`);
      }
      setShowNavPane(false);
    },
    [tocItems],
  );

  useEffect(() => {
    if (!activeEditor || !isDirty) return;

    const timer = setTimeout(async () => {
      try {
        const currentContent = JSON.stringify(fullTipTapJSON);

        if (currentContent === lastSavedContent.current) {
          setIsDirty(false);
          return;
        }

        const payload = {
          document_id: reportID,
          work_order_id: workOrder,
          save_type: "autosave",
          content: fullTipTapJSON,
        };

        const data = await ManualTipTapSave({
          authAxios,
          report_result_id: reportID,
          save_type: "autosave",
          content: fullTipTapJSON,
        });

        localStorage.setItem("document", JSON.stringify(payload));

        lastSavedContent.current = currentContent;
        setIsDirty(false);
      } catch (error) {
        console.error("Error autosaving document:", error);
      }
    }, 5000);

    return () => clearTimeout(timer);
  }, [
    activeEditor,
    isDirty,
    pageHtmlChunks,
    reportID,
    workOrder,
    authAxios,
    ManualTipTapSave,
    getFullDocumentTipTapJSON,
  ]);

  const handleSave = async () => {
    if (!pageHtmlChunks || pageHtmlChunks.length === 0) return;

    // const fullTipTapJSON = getFullDocumentTipTapJSON();

    const payload = {
      document_id: reportID,
      work_order_id: workOrder,
      save_type: "manual",
      content: fullTipTapJSON,
    };

    try {
      // -----------------------------------
      // 1. SAVE THE DOCUMENT
      // -----------------------------------
      await ManualTipTapSave({
        authAxios,
        report_result_id: reportID,
        save_type: "manual",
        content: fullTipTapJSON,
      });

      console.log("Document saved successfully");

      if (isNewReport) {
        // If saveNewReport is your specific mutation/API call, call it here:
        // await saveNewReport(reportID, payload);

        setIsNewReport(false);

        // Update URL so browser reloads stay in continue mode
        if (isNewReport) {
          setIsNewReport(false);

          history.replace(
            `/reports/report_writers_agenda/tiptap/${reportID}?mode=continue&status=${statusParam}&role=${role}&url_param=${urlParam}`,
          );
        }
      }

      // -----------------------------------
      // 2. GET UPDATED REPORT DATA
      // -----------------------------------
      const continueResponse = await await continueReport({
        reportId: Number(reportID),
        template: urlParam,
      });

      console.log("Continue report response:", continueResponse);

      // -----------------------------------
      // 3. UPDATE TIPTAP STATE
      // -----------------------------------
      setReportData(continueResponse);

      setReportVersion(continueResponse?.latest_version);

      setVersionHistory(continueResponse?.version_history);
      setReportNumber(continueResponse?.report_number || "");

      // -----------------------------------
      // 4. REPORT IS NOW AN EXISTING REPORT
      // -----------------------------------
      setIsNewReport(false);

      localStorage.setItem("document", JSON.stringify(payload));

      lastSavedContent.current = JSON.stringify(fullTipTapJSON);

      setIsDirty(false);

      toast.success("Document saved successfully!");
    } catch (error: any) {
      console.error("Error saving document:", error);

      toast.error(
        error?.response?.data?.error ||
          "Failed to save document. Please try again.",
      );
    }
  };

  const handleAddComment = () => {
    if (!activeEditor) return;

    const { from, to } = activeEditor.state.selection;

    if (from === to) {
      toast.info("Please select some text, table, or image to add a comment.");
      return;
    }

    const selectedText = activeEditor.state.doc.textBetween(from, to, " ");

    let type: CommentType = "text";

    activeEditor.state.doc.nodesBetween(from, to, (node: any) => {
      if (node.type.name === "table") {
        type = "table";
      }

      if (node.type.name === "image") {
        type = "image";
      }
    });

    setCommentRange({ from, to });
    setSelectedText(selectedText);
    setCommentType(type);
    setCommentText("");
    setShowCommentBox(true);
  };

  const handleSaveComment = async () => {
    if (!commentRange || !commentText.trim()) return;

    try {
      // Corrected API call with proper payload property for the comment text
      await AddComments({
        authAxios,
        report_result_id: reportID,
        commented_against_text: selectedText,
        comment: commentText.trim(), // Replaced the placeholder with the actual state variable
      });

      const newComment: Comment = {
        id: `${Date.now()}-${Math.random()}`,
        from: commentRange.from,
        to: commentRange.to,
        type: commentType,
        selectedText,
        comment: commentText.trim(),
        createdAt: new Date().toISOString(),
      };

      setComments((prev) => [...prev, newComment]);

      // Update editor content with highlight and comment icon
      activeEditor
        ?.chain()
        .focus()
        .setTextSelection({
          from: commentRange.from,
          to: commentRange.to,
        })
        .setHighlight()
        .insertContentAt(commentRange.to, {
          type: "commentIcon",
          attrs: { "data-comment-id": newComment.id },
        })
        .run();

      setShowCommentBox(false);
      setCommentText("");
      setSelectedText("");
      setCommentRange(null);
      setCommentType("text");

      toast.success("Comment added successfully!");
    } catch (error: any) {
      console.error("Failed to add comment:", error);
      toast.error(error?.response?.data?.error || "Failed to add comment.");
    }
  };

  const handlePrint = () => {
    const editorCanvas = document.querySelector(".docgen-editor-canvas");

    if (!editorCanvas) {
      toast.error("Could not find document to print.");
      return;
    }

    const clone = editorCanvas.cloneNode(true) as HTMLElement;
    clone
      .querySelectorAll("lsdb-button, .lsdb-button-node")
      .forEach((el) => el.remove());

    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      toast.error("Please allow pop-ups to print.");
      return;
    }

    printWindow.document.write(`
      <html>
        <head>
          <title>${reportData?.document_name || "Print Document"}</title>
          <style>
            @page { size: A4 portrait; margin: 0; }
            body {
              margin: 0;
              font-family: "Aptos", Calibri, Arial, sans-serif;
              font-size: 11pt;
              background: #fff;
              -webkit-print-color-adjust: exact; 
              print-color-adjust: exact;
            }
            .docgen-editor-canvas > div { transform: none !important; }
            .docgen-page {
              box-shadow: none !important;
              border: none !important;
              margin: 0 auto !important;
              page-break-after: always;
            }
            .docgen-page:last-of-type {
              page-break-after: auto;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin: 12px 0;
              table-layout: fixed;
            }
            th, td {
              border: 1px solid black;
              padding: 4px 8px;
              word-break: break-word;
              font-size: 10pt;
            }
            th {
              text-align: center;
              font-weight: bold;
              -webkit-print-color-adjust: exact; 
              color-adjust: exact;
            }
            img {
              max-width: 100%;
              display: block;
              margin: 0 auto;
            }
            /* Keep cover-page image + Report Information table together on
               page 1 in the printed/PDF output too (see the matching rule
               in the editor's own stylesheet for the full rationale). The
               first element on the cover page (when it's an image) bleeds
               to the true page edges here as well; its resize/rotation is
               left untouched (no forced sizing). */
            .docgen-page:first-of-type .docgen-page-content .ProseMirror > :first-child:has(img) {
              margin: -20px -80px 0 -80px !important;
              width: calc(100% + 160px) !important;
            }
            /* Zero out the image node's own inline alignment margin (set by
               ImageNodeView's NodeViewWrapper, e.g. "8px auto 8px 0") so no
               residual gap remains between the page edge and the bled
               image once the paragraph above is flush with it. */
            .docgen-page:first-of-type .docgen-page-content .ProseMirror > :first-child:has(img) > [data-alignment] {
              margin: 0 !important;
            }
            .docgen-page:first-of-type table {
              page-break-inside: avoid;
            }
          </style>
        </head>
        <body>
          <div class="docgen-editor-canvas">
            ${clone.innerHTML}
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();

    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  useEffect(() => {
    if (!activeEditor) {
      setCurrentPageNumber(1);
      return;
    }

    const pageEditors = Array.from(
      document.querySelectorAll(".docgen-page-content .ProseMirror"),
    ) as HTMLElement[];

    const currentPageIndex = pageEditors.findIndex(
      (pageElement) => pageElement === activeEditor.view?.dom,
    );

    if (currentPageIndex >= 0) {
      setCurrentPageNumber(currentPageIndex + 1);
    }
  }, [activeEditor, pageHtmlChunks.length]);

  const handleDeleteCurrentPage = useCallback(() => {
    if (!activeEditor) {
      toast.info("Please place the cursor on a page first.");
      return;
    }

    const pageEditors = Array.from(
      document.querySelectorAll(".docgen-page-content .ProseMirror"),
    ) as HTMLElement[];

    const currentPageIndex = pageEditors.findIndex(
      (el) => el === activeEditor.view?.dom,
    );

    if (currentPageIndex === -1) {
      toast.error("Unable to determine the current page.");
      return;
    }

    if (pageHtmlChunks.length <= 1) {
      toast.info("The document must contain at least one page.");
      return;
    }

    if (currentPageIndex === 0) {
      toast.info("The cover page cannot be deleted.");
      return;
    }

    setDeletePageIndex(currentPageIndex);
    setDeletePageNumber(currentPageIndex + 1);
    setShowDeletePageDialog(true);
  }, [activeEditor, pageHtmlChunks.length]);

  const confirmDeleteCurrentPage = useCallback(() => {
    if (deletePageIndex === null) return;

    const indexToDelete = deletePageIndex;
    const pageNumber = indexToDelete + 1;

    setPageHtmlChunks((prev) => {
      if (prev.length <= 1) return prev;
      const updated = [...prev];
      updated.splice(indexToDelete, 1);
      return updated;
    });

    setIsDirty(true);
    setActiveEditor(null);
    setShowDeletePageDialog(false);
    setDeletePageIndex(null);
    setDeletePageNumber(null);

    // Re-activate an editor on the page that now sits at this position
    setTimeout(() => {
      const els = document.querySelectorAll(
        ".docgen-page-content .ProseMirror",
      );
      const target = els[Math.min(indexToDelete, els.length - 1)] as any;
      if (target?.editor) setActiveEditor(target.editor);
    }, 100);

    toast.success(
      `Page ${pageNumber} deleted. Click Save to keep this change.`,
    );
  }, [deletePageIndex]);

  const handleUserEdit = useCallback(() => {
    setIsDirty(true);
  }, []);

  if (!openRow) {
    return (
      <div>
        Loading agenda item... <LinearProgress />
      </div>
    );
  }

  return (
    <>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100vh" /* Forces the container to take full screen height */,
          width: "100%" /* <-- CRITICAL: Forces exactly viewport width */,
          maxWidth:
            "100%" /* <-- CRITICAL: Prevents vertical scrollbar from causing horizontal overflow */,
          overflow: "hidden" /* <-- Strictly kills browser-level scrollbars */,
          backgroundColor: "#ffffff",
          boxSizing: "border-box",
        }}
      >
        <style>{`
        @font-face {
          font-family: "Aptos";
          src: url("/Fonts/Aptos.ttf") format("truetype"); 
          font-weight: 400;
          font-style: normal;
        }
          html, body {
          margin: 0;
          height: 100%;
          overflow: hidden;
        }

        @font-face {
          font-family: "Aptos";
          src: url("/Fonts/Aptos-Bold.ttf") format("truetype"); 
          font-weight: 700;
          font-style: normal;
        }

        @font-face {
          font-family: "Aptos";
          src: url("/Fonts/Aptos-Italic.ttf") format("truetype"); 
          font-weight: 400;
          font-style: italic;
        }

        @font-face {
          font-family: "Aptos";
          src: url("/Fonts/Aptos-Bold-Italic.ttf") format("truetype"); 
          font-weight: 700;
          font-style: italic;
        }

        .docgen-editor-canvas {
          flex: 1;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 30px 20px 60px;
          background: #f5f4f0;
        }

        .ProseMirror {
          outline: none;
          color: #000;
          font-family: "Aptos" !important;
          font-size: 11pt;
          line-height: 1;
          word-wrap: break-word;
          min-height: auto
        }

              .ProseMirror a[data-action="open-image-drawer"] {
          cursor: pointer !important;
          color: #1976D2 !important;
          text-decoration: underline !important;
        }

        .ProseMirror p { margin: 0 0 8px; }

        .ProseMirror th[data-text-direction = 'True']{
            writing-mode: vertical-rl;
            transform: rotate(180deg);
            white-space: normal;
            vertical-align: middle !important;
            text-align: center !important;
          }

        .docgen-page-content .ProseMirror table { 
          width: 100% !important; 
          border-collapse: collapse !important; 
          margin: 8px 0 !important; 
          table-layout: fixed !important;
        }

        .docgen-page-content .ProseMirror th, 
        .docgen-page-content .ProseMirror td { 
          border: 1px solid #000 !important; 
          padding: 2px 5px !important;
          vertical-align: middle !important; 
          word-break: break-word !important;
          font-size: 8pt !important;
          line-height: 1.1 !important;
          font-weight:normal;
        }

        /* 2. HEADER & FOOTER TABLES: Invisible layout, greyish text */
        .docgen-page-header .ProseMirror table,
        .docgen-page-footer .ProseMirror table {
          width: 100% !important;
          border-collapse: collapse !important;
          margin: 0 !important;
        }

        .docgen-page-header .ProseMirror th,
        .docgen-page-header .ProseMirror td,
        .docgen-page-footer .ProseMirror th,
        .docgen-page-footer .ProseMirror td {
          border: none !important; /* Removes the rigid black boxes */
          padding: 0 4px !important;
          vertical-align: bottom !important;
          font-size: 9pt !important;
          color: #777 !important; /* Applies the grayish text color */
          font-weight:normal;
        }
        
        .docgen-page-header .ProseMirror p,
        .docgen-page-footer .ProseMirror p {
            margin: 0 !important; /* Removes paragraph gaps in the header/footer */
        }

        /* 3. IMAGE ALIGNMENT: Allows right-aligned logos in header */
        .docgen-page-content .ProseMirror img { 
          max-width: 100%; 
          height: auto; 
          display: block;
          margin: 0 auto; /* Centered in body */
        }

        /* COVER HERO IMAGE: when the very first element on the cover page
           is an image, let it bleed to the true page edges (ignoring the
           page's normal 20px/80px content padding) while every other
           element on the page — heading, tables, later images, etc. —
           keeps the standard padding/margin untouched. This intentionally
           does NOT force any width/height/rotation on the image anymore,
           so resizing and rotating the cover image works exactly like on
           any other page. */
        .docgen-page:first-of-type .docgen-page-content .ProseMirror > :first-child:has(img) {
          margin: -20px -80px 0 -80px !important;
          width: calc(100% + 160px) !important;
        }
        /* Zero out the image node's own inline alignment margin (set by
           ImageNodeView's NodeViewWrapper, e.g. "8px auto 8px 0") so no
           residual gap remains between the page edge and the bled image
           once the paragraph above is flush with it. */
        .docgen-page:first-of-type .docgen-page-content .ProseMirror > :first-child:has(img) > [data-alignment] {
          margin: 0 !important;
        }
        /* Keep the cover's Report Information table intact if the printed
           page boundary lands mid-table. */
        .docgen-page:first-of-type .ProseMirror table {
          page-break-inside: avoid;
        }

        /* ADD THIS SPECIFIC OVERRIDE FOR IMAGES WITH ATTRIBUTES */
          .ProseMirror img[width] {
            width: attr(width, length, 50%); /* Fallback or attribute reader */
            max-width: 100%;
          }

          /* Ensure container node views (if using ResizableImage) shrink to fit */
          .ProseMirror .image-resizer,
          .ProseMirror [data-drag-handle] {
            max-width: 100% !important;
            display: inline-block;
          }
                  
        .docgen-page-header .ProseMirror img,
        .docgen-page-footer .ProseMirror img {
          display: inline-block !important; /* Allows text-align: right to work */
          margin: 0 !important;
          max-height: 25px !important; /* Sizes the logo perfectly */
          width: auto !important;
        }
          
        .ProseMirror td:has(img) {
          padding: 5px !important;
          line-height: 10px !important;
        }

        .ProseMirror table { 
          width: 100% !important; 
          border-collapse: collapse !important; 
          margin: 8px 0 !important; 
          table-layout: fixed !important;
        }

        .ProseMirror th, 
        .ProseMirror td { 
          border: 1px solid #000 !important; 
          padding: 2px 5px !important;
          vertical-align: middle !important; 
          word-break: break-word !important;
          font-size: 8pt !important;
          line-height: 1.1 !important;
        }

        .ProseMirror td p, 
        .ProseMirror th p {
          margin: 0 !important;
          line-height: 1.6 !important;
        }
          .docgen-editor-canvas .ProseMirror table .selectedCell {
          position: relative;
          box-shadow: inset 0 0 0 9999px rgba(106, 164, 223, 0.25);
        }

      .docgen-editor-canvas .ProseMirror table .selectedCell::after {
        content: "";
        position: absolute;

        left: 0;
        top: 0;
        right: 0;
        bottom: 0;

        width: 100%;
        height: 100%;

        background: rgba(25, 118, 210, 0.2);

        pointer-events: none;
      }

        .ProseMirror img { 
          max-width: 100%; 
          height: auto; 
          display: block;
          margin: 0 auto;
        }

        .ProseMirror td:has(img) {
          padding: 5px !important;
          line-height: 10px !important;
        }

        .ProseMirror td:has(img) p {
          margin: 0 !important;
          line-height: 0px !important;
        }

        @media print {
          @page { size: A4 portrait; margin: 0; }
          body { background: #fff !important; }
          .docgen-page { box-shadow: none !important; border: none !important; margin-bottom: 0 !important; page-break-after: always; }
          .docgen-page:last-of-type { page-break-after: auto; }
        }
          .ProseMirror lsdb-button button,
        .ProseMirror .lsdb-button-node button {
          padding: 4px 4px !important;
          font-size: 6pt !important;
          min-height: 18px !important;
          height: auto !important;
        }

        /* If the button uses an SVG icon, shrink the icon too */
        .ProseMirror lsdb-button svg,
        .ProseMirror .lsdb-button-node svg {
          width: 10px !important;
          height: 10px !important;
        }
          /* --- Column resize handle (the vertical drag line between columns) --- */
.ProseMirror .tableWrapper {
  overflow-x: auto;
}

.ProseMirror table {
  position: relative;
}

.ProseMirror td,
.ProseMirror th {
  position: relative;
}

.ProseMirror .column-resize-handle {
  position: absolute;
  right: -2px;
  top: 0;
  bottom: -2px;
  width: 2px;
  z-index: 20;
  background-color: #1976D2;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.1s ease;
}

/* Show it while the mouse is actually over that column border */
.ProseMirror td:hover > .column-resize-handle,
.ProseMirror th:hover > .column-resize-handle,
.ProseMirror .column-resize-handle {
  opacity: 1;
}

/* Cursor + handle color while actively dragging */
.ProseMirror.resize-cursor {
  cursor: col-resize;
}
.ProseMirror.resize-cursor .column-resize-handle {
  background-color: #101212;
  opacity: 0;
}
      `}</style>
        {/* 1. TITLE BAR & TOOLBAR (Sticky Top) */}
        <div
          style={{
            flexShrink: 0,
            width: "100%",
            maxWidth: "100%",
            background: "#fff",
            zIndex: 50,
            boxSizing: "border-box",
            overflow: "visible",
          }}
        >
          <TipTapTitleBar
            title={reportNumber}
            version={reportVersion}
            versionHistory={version_history}
            status={openRow?.st}
            ProjectId={ProjectId}
            onBack={() => window.history.back()}
            workOrder={workOrder}
            isMovingWriterToReviewer={isMovingWriterToReviewer}
            onSubmit={handleSubmit}
            reportImages={reportImages}
            selectedImages={selectedImages}
            onImageSelect={handleImageSelect}
            onInsertImages={handleInsertImages}
            role={role}
            feedbackCount={sidePanelBadgeCount}
            onChecklist={() => setShowSidePanelDrawer(true)}
            onComments={() => setShowCommentsPanel(true)}
          />

          {activeEditor && (
            <TipTapToolbar
              editor={activeEditor}
              onSave={handleSave}
              onPrint={handlePrint}
              onDeletePage={handleDeleteCurrentPage}
              onConfirmDeletePage={confirmDeleteCurrentPage}
              currentPageNumber={currentPageNumber}
              zoom={zoom}
              setZoom={setZoom}
              darkMode={darkMode}
              toggleTheme={toggleTheme}
              workorder={workOrder}
              handleAddComment={handleAddComment}
              showSidePanel={showSidePanel}
              setShowSidePanelDrawer={setShowSidePanelDrawer}
              sidePanelBadgeCount={sidePanelBadgeCount}
              onOpenFindReplace={() => setShowFindReplace(true)}
              onOpenNavPane={() => setShowNavPane(true)}
            />
          )}
        </div>

        {/* 2. EDITOR + CHECKLIST SPLIT (Bottom Section) */}
        <div
          style={{
            display: "flex",
            flex: 1,
            width: "100%" /* <-- ADD THIS */,
            minWidth: 0 /* <-- CRITICAL: Allows flex child to shrink */,
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          {/* EDITOR (Left Side - Takes up remaining width) */}
          <div
            className="docgen-editor-canvas"
            style={{
              flex: "1 1 auto",
              minWidth: 0,
              minHeight: 0,
              width: "100%",
              overflowY: "auto",
              overflowX: "hidden",
              background: darkMode ? "#27272a" : "#f5f4f0",
              boxSizing: "border-box",
              padding: "30px 20px 60px",
            }}
          >
            <div
              style={{
                transform: `scale(${zoom / 100})`,
                transformOrigin: "top center",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                maxWidth: "100%",
              }}
            >
              {isDocumentLoading ? (
                <div
                  style={{
                    padding: "40px",
                    color: "#888",
                    fontStyle: "italic",
                  }}
                >
                  Loading document...
                </div>
              ) : (
                pageHtmlChunks.map((htmlChunk, pageIndex) => (
                  <PageChrome
                    key={pageIndex}
                    chrome={defaultChromeConfig}
                    contentHeightPx={contentHeightPx}
                    pageNumber={pageIndex + 1}
                    totalPages={pageHtmlChunks.length + 1}
                    isFirstPage={pageIndex === 0}
                    customHeaderHtml={docHeaderHtml}
                    customFooterHtml={docFooterHtml}
                  >
                    <SinglePageEditor
                      contentHtml={htmlChunk}
                      maxHeightPx={
                        pageIndex === 0
                          ? firstPageContentHeightPx
                          : contentHeightPx
                      }
                      onChange={(newHtml) =>
                        handlePageChunkChange(pageIndex, newHtml)
                      }
                      onFocus={handleEditorFocus}
                      onUserEdit={handleUserEdit}
                      onOverflow={(html) => handlePageOverflow(pageIndex, html)}
                    />
                  </PageChrome>
                ))
              )}
            </div>
          </div>

          {/* CHECKLIST (Right Side - Fixed Width) */}
          {isReviewer && !isDocumentLoading && (
            <Box
              style={{
                width: 380,
                maxWidth: "100%",
                height: "100%",
                borderLeft: "1px solid #E5E7EB",
                backgroundColor: "#fff",

                overflowX: "hidden",
                flexShrink: 0,
                boxSizing: "border-box",
              }}
            >
              <SidePanel
                ctl={ctl}
                r={openRow}
                role={role}
                onTransition={(kind) => handleTransition(openRow, kind)}
                reportID={reportID}
                fullTipTapJSON={fullTipTapJSON}
                checklistData={checklistData}
                isLoading={isLoadingchecklistData}
              />
            </Box>
          )}
        </div>
      </div>
      <Dialog
        open={showCommentBox}
        onClose={() => setShowCommentBox(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Comment</DialogTitle>

        <DialogContent>
          <Typography
            variant="body2"
            style={{
              marginBottom: 12,
              color: "#555",
            }}
          >
            Selected: "{selectedText}"
          </Typography>

          <TextField
            fullWidth
            multiline
            minRows={4}
            variant="outlined"
            placeholder="Enter your comment..."
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
          />
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              setShowCommentBox(false);
              setCommentText("");
            }}
            color="default"
          >
            Cancel
          </Button>

          <Button
            onClick={handleSaveComment}
            color="primary"
            variant="contained"
            disabled={!commentText.trim()}
          >
            Add Comment
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showDeletePageDialog}
        onClose={() => setShowDeletePageDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Page</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete{" "}
            <strong>page {deletePageNumber}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowDeletePageDialog(false);
              setDeletePageIndex(null);
              setDeletePageNumber(null);
            }}
            color="default"
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteCurrentPage}
            color="secondary"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Drawer
        anchor="right"
        open={showSidePanelDrawer}
        onClose={() => setShowSidePanelDrawer(false)}
      >
        <Box width={380} role="presentation">
          <SidePanel
            ctl={ctl}
            r={openRow}
            role={role}
            onTransition={(kind) => handleTransition(openRow, kind)}
            reportID={reportID}
            fullTipTapJSON={fullTipTapJSON}
            checklistData={
              isReviewer
                ? checklistData
                : isWriter && statusParam === "corr_rev"
                  ? pendingCheckList
                  : isWriter && statusParam === "corr_ver"
                    ? verifierData
                    : role === "verifier" && statusParam === "verify"
                      ? verifierData
                      : undefined
            }
            isLoading={
              isReviewer
                ? isLoadingchecklistData
                : isWriter && statusParam === "corr_rev"
                  ? isLoadingPendingCheckList
                  : isWriter && statusParam === "corr_ver"
                    ? isLoadingverifierData
                    : false
            }
          />
        </Box>
      </Drawer>

      <Drawer
        anchor="left"
        open={showNavPane}
        onClose={() => setShowNavPane(false)}
      >
        <Box width={300} role="presentation" style={{ padding: 16 }}>
          <Typography
            variant="subtitle1"
            style={{ fontWeight: 600, marginBottom: 8 }}
          >
            {tocItems.length > 0 ? "Table of Contents" : "Report Sections"}
          </Typography>
          <Divider />
          {tocItems.length > 0 ? (
            <TableOfContents
              items={tocItems}
              title={null}
              onNavigate={() => setShowNavPane(false)}
            />
          ) : navHeadings.length === 0 ? (
            <Typography
              variant="body2"
              style={{ color: "#888", padding: "16px 0" }}
            >
              No headings found. Use Heading 1/2/3 styles to build a navigable
              outline.
            </Typography>
          ) : (
            <List dense>
              {navHeadings.map((heading, idx) => (
                <ListItem
                  key={idx}
                  button
                  onClick={() => handleNavHeadingClick(heading)}
                  style={{ paddingLeft: 8 + (heading.level - 1) * 16 }}
                >
                  <ListItemText
                    primary={heading.text}
                    primaryTypographyProps={{
                      style: {
                        fontSize: heading.level === 1 ? 14 : 13,
                        fontWeight: heading.level === 1 ? 600 : 400,
                      },
                    }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Drawer>

      <Drawer
        anchor="right"
        open={showCommentsPanel}
        onClose={() => setShowCommentsPanel(false)}
      >
        <Box
          width={380}
          role="presentation"
          style={{
            height: "100%",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            backgroundColor: "#F9FAFB", // Slight off-white background behind cards
          }}
        >
          {(() => {
            // Mapping your API response
            const apiComments = Array.isArray(verifierData)
              ? verifierData.map((item: any) => ({
                  id: item.id,
                  text: item.comment || "",
                  anchor: item.selected_text || "",
                  by: item.comment_created_by?.split("@")[0] || "User", // Simplifies email to username like "edgar.ibarra"
                  at: item.comment_created_on
                    ? moment(item.comment_created_on).format("DD-MM-YYYY HH:mm")
                    : "",
                  stage: statusParam === "approval" ? "approval" : "verify",
                  held: false,
                  ack: item.is_acknowledged === true, // maps true/false to cm.ack
                }))
              : [];

            const stage = statusParam === "approval" ? "approval" : "verify";
            const mode =
              role === "writer"
                ? "writer"
                : role === "verifier" || role === "reviewer"
                  ? "gate"
                  : "copy";
            const controllerComments = ctl.cmFor(stage);
            const all =
              apiComments.length > 0 ? apiComments : controllerComments;
            const sent = ctl.cmSent(stage);
            const ack = ctl.cmAck(stage);
            const DeleteComments = async (id: any, anchor?: string) => {
              try {
                // 1. Delete via API
                await DocuCommentsdelete({
                  authAxios,
                  note_id: id,
                });

                // 2. Refresh the comments list in the sidebar
                await refetchVerifierComments();

                // 3. Trigger the editor to locally un-highlight text and remove the icon
                window.dispatchEvent(
                  new CustomEvent("remove-comment-from-editor", {
                    detail: {
                      commentId: String(id),
                      anchorText: anchor || "",
                    },
                  }),
                );

                toast.success("Comment is deleted successfully.");
              } catch (error: any) {
                console.error("Delete comment failed:", error);

                toast.error(
                  error?.response?.data?.error ||
                    error?.response?.data?.message ||
                    "Failed to delete the comment.",
                );
              }
            };

            return (
              <>
                {/* =====================================================
                    HEADER
                ====================================================== */}
                <div
                  style={{
                    padding: "20px",
                    backgroundColor: "#fff",
                    borderBottom: "1px solid #E5E7EB",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "8px",
                    }}
                  >
                    <h3
                      style={{
                        margin: 0,
                        fontSize: "18px",
                        fontWeight: 500,
                        color: "#111827",
                      }}
                    >
                      Comments
                    </h3>
                    <span style={{ fontSize: "14px", color: "#9CA3AF" }}>
                      {all.length}
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: "13px",
                      color: "#6B7280",
                      lineHeight: 1.4,
                    }}
                  >
                    Highlight text in the report to anchor a comment. Nothing
                    reaches the writer until you send.
                  </div>
                </div>

                {/* =====================================================
                    BODY / COMMENTS LIST
                ====================================================== */}
                <div
                  style={{
                    flex: 1,
                    overflowY: "auto",
                    padding: "16px",
                  }}
                >
                  {all.length > 0 ? (
                    all.map((cm: any) => (
                      <div
                        key={cm.id}
                        style={{
                          position: "relative",
                          border: "1px solid #C4D7C1", // Light green border
                          borderRadius: "8px",
                          padding: "16px",
                          marginBottom: "12px",
                          backgroundColor: "#fff",
                        }}
                      >
                        {/* Delete Comment Button */}
                        <IconButton
                          size="small"
                          aria-label="Delete comment"
                          title="Delete comment"
                          onClick={() => DeleteComments(cm.id, cm.anchor)}
                          style={{
                            position: "absolute",
                            top: "8px",
                            right: "8px",
                            color: "#e53935",
                            zIndex: 10,
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                        {/* Top row: User info & Tags */}
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: "8px",
                            marginBottom: "4px",
                            paddingRight: "32px",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                            }}
                          >
                            <MI name="person" size={16} color="#6B7280" />
                            <span
                              style={{
                                fontSize: "14px",
                                fontWeight: 500,
                                color: "#111827",
                              }}
                            >
                              {cm.by}
                            </span>
                          </div>

                          {/* VERIFICATION Tag (Always visible) */}
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              backgroundColor: "#FFF7ED",
                              color: "#C2410C",
                              padding: "2px 8px",
                              borderRadius: "4px",
                              fontSize: "11px",
                              fontWeight: 600,
                            }}
                          >
                            <MI name="assignment" size={12} color="#C2410C" />{" "}
                            VERIFICATION
                          </div>

                          {/* FIXED Tag (Only shows if is_acknowledged is true) */}
                          {cm.ack && (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                                backgroundColor: "#ECFDF5",
                                color: "#047857",
                                padding: "2px 8px",
                                borderRadius: "4px",
                                fontSize: "11px",
                                fontWeight: 600,
                              }}
                            >
                              <MI name="check" size={12} color="#047857" />{" "}
                              FIXED
                            </div>
                          )}
                        </div>

                        {/* Timestamp */}
                        <div
                          style={{
                            textAlign: "right",
                            fontSize: "12px",
                            color: "#9CA3AF",
                            fontFamily: "monospace",
                            marginBottom: "12px",
                          }}
                        >
                          {cm.at}
                        </div>

                        {/* Anchor Text */}
                        {cm.anchor && (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: "6px",
                              marginBottom: "8px",
                              color: "#D97706",
                              fontSize: "13px",
                            }}
                          >
                            <div style={{ marginTop: "2px" }}>
                              <MI name="link" size={14} color="#D97706" />
                            </div>
                            <span>{cm.anchor}</span>
                          </div>
                        )}

                        {/* Comment Text */}
                        <div
                          style={{
                            fontSize: "14px",
                            color: "#374151",
                            lineHeight: 1.5,
                            marginBottom: "12px",
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          {cm.text}
                        </div>

                        {/* Footer Status inside card (Only shows if is_acknowledged is true) */}
                        {cm.ack && (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              fontSize: "13px",
                              color: "#047857",
                            }}
                          >
                            <MI name="person" size={14} color="#047857" />
                            Writer marked this fixed
                          </div>
                        )}

                        {/* Writer Action (if not acknowledged and mode is writer) */}
                        {!cm.ack && mode === "writer" && (
                          <div
                            style={{
                              marginTop: "12px",
                              borderTop: "1px solid #F3F4F6",
                              paddingTop: "12px",
                            }}
                          >
                            <button
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                background: "none",
                                border: "1px solid #D1D5DB",
                                padding: "6px 12px",
                                borderRadius: "6px",
                                cursor: "pointer",
                                fontSize: "13px",
                                fontWeight: 500,
                              }}
                              onClick={async () => {
                                try {
                                  // 1. Call the API to acknowledge this specific comment
                                  await authAxios.get(
                                    urls.reportWriterAgenda.AcknowledgeComments(
                                      cm.id,
                                    ),
                                  );

                                  // 2. Update local state to instantly show the "FIXED" label
                                  ctl.dispatch({
                                    type: "ACK_COMMENT",
                                    id: cm.id,
                                  });
                                  await refetchVerifierComments();
                                  toast.success("Comment marked as fixed.");
                                } catch (error: any) {
                                  console.error(
                                    "Failed to mark comment as fixed:",
                                    error,
                                  );
                                  toast.error(
                                    error?.response?.data?.error ||
                                      "Failed to mark comment as fixed.",
                                  );
                                }
                              }}
                            >
                              <MI name="check" size={14} /> Mark as fixed
                            </button>
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div
                      style={{
                        padding: "40px 10px",
                        textAlign: "center",
                        color: "#9CA3AF",
                      }}
                    >
                      <p>No comments yet</p>
                    </div>
                  )}
                </div>

                {/* =====================================================
                    FOOTER ACTIONS
                ====================================================== */}
                <div
                  style={{
                    padding: "16px",
                    backgroundColor: "#fff",
                    borderTop: "1px solid #E5E7EB",
                  }}
                >
                  {/* Status Note */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      color: "#6B7280",
                      fontSize: "13px",
                      marginBottom: "16px",
                    }}
                  >
                    <MI
                      name={
                        all.every((cm: any) => cm.ack) ? "checkCircle" : "clock"
                      }
                      size={16}
                      color="#6B7280"
                    />
                    <span>
                      {all.filter((cm: any) => cm.ack).length} of {all.length}{" "}
                      fixed by {openRow?.writer || "writer"}.
                    </span>
                  </div>

                  {/* Action Buttons */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                    }}
                  >
                    {/* Add Comment Button (Only for Verifier/Approver) */}
                    {/* {mode !== "writer" && (
                      <button
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "8px",
                          width: "100%",
                          padding: "10px",
                          backgroundColor: "#fff",
                          border: "1px solid #D1D5DB",
                          borderRadius: "6px",
                          fontSize: "14px",
                          fontWeight: 500,
                          color: "#374151",
                          cursor: "pointer",
                        }}
                        onClick={() => ctl.dispatch({ type: "START_COMMENT" })}
                      >
                        <MI name="message" size={16} /> Add comment
                      </button>
                    )} */}

                    {/* Primary Action Buttons */}
                    {mode === "writer" ? (
                      /* ---------------- WRITER BUTTON ---------------- */
                      <button
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "8px",
                          width: "100%",
                          padding: "10px",
                          backgroundColor:
                            all.length === 0 || all.every((cm: any) => cm.ack)
                              ? "#166534"
                              : "#9CA3AF",
                          border: "none",
                          borderRadius: "6px",
                          fontSize: "14px",
                          fontWeight: 500,
                          color: "#fff",
                          cursor:
                            all.length === 0 || all.every((cm: any) => cm.ack)
                              ? "pointer"
                              : "not-allowed",
                        }}
                        disabled={
                          !(all.length === 0 || all.every((cm: any) => cm.ack))
                        }
                        onClick={async () => {
                          try {
                            const sentTo =
                              statusParam === "corr_ver"
                                ? "verifier"
                                : "approver";

                            await ManualTipTapSave({
                              authAxios,
                              report_result_id: reportID,
                              save_type: "manual",
                              content: fullTipTapJSON,
                            });
                            await authAxios.post(urls.docgen.moveReport, {
                              report_result_id: reportID,
                              sent_from: "writer",
                              sent_to: sentTo,
                            });

                            toast.success(`Passed to ${sentTo} successfully.`);
                            handleTransition(
                              openRow,
                              sentTo === "verifier" ? "verify" : "approval",
                            );
                            setShowCommentsPanel(false);
                            history.push("/reports/report_module");
                          } catch (error: any) {
                            console.error("Failed to send report:", error);
                            toast.error(
                              error?.response?.data?.error ||
                                "Failed to submit report.",
                            );
                          }
                        }}
                      >
                        <MI name="send" size={16} color="#fff" />
                        {statusParam === "corr_ver"
                          ? "Pass to verifier"
                          : "Pass to approver"}
                      </button>
                    ) : /* ---------------- VERIFIER / APPROVER BUTTONS ---------------- */
                    all.length === 0 || all.every((cm: any) => cm.ack) ? (
                      <button
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "8px",
                          width: "100%",
                          padding: "10px",
                          backgroundColor: "#166534",
                          border: "none",
                          borderRadius: "6px",
                          fontSize: "14px",
                          fontWeight: 500,
                          color: "#fff",
                          cursor: "pointer",
                        }}
                        onClick={async () => {
                          try {
                            // Dynamically set roles based on the current stage
                            const sentFrom =
                              stage === "verify" ? "verifier" : "approver";
                            const sentTo =
                              stage === "verify" ? "approver" : "completed";

                            await ManualTipTapSave({
                              authAxios,
                              report_result_id: reportID,
                              save_type: "autosave",
                              content: fullTipTapJSON,
                            });

                            // Call the API to move the report forward
                            await authAxios.post(urls.docgen.moveReport, {
                              report_result_id: reportID,
                              sent_from: sentFrom,
                              sent_to: sentTo,
                            });

                            toast.success(
                              `Report successfully passed to ${sentTo}.`,
                            );

                            // Update local state and close drawer
                            handleTransition(
                              openRow,
                              stage === "verify" ? "approval" : "approved",
                            );
                            setShowCommentsPanel(false);

                            history.push("/reports/report_module");
                          } catch (error: any) {
                            console.error("Failed to pass report:", error);
                            toast.error(
                              error?.response?.data?.error ||
                                "Failed to pass report.",
                            );
                          }
                        }}
                      >
                        <MI name="arrowRight" size={16} color="#fff" />
                        {stage === "verify"
                          ? "Pass to approver"
                          : "Approve report"}
                      </button>
                    ) : (
                      <button
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "8px",
                          width: "100%",
                          padding: "10px",
                          backgroundColor: "#B45309",
                          border: "none",
                          borderRadius: "6px",
                          fontSize: "14px",
                          fontWeight: 500,
                          color: "#fff",
                          cursor: "pointer",
                        }}
                        onClick={async () => {
                          try {
                            const sentFrom =
                              stage === "verify" ? "verifier" : "approver";

                            await ManualTipTapSave({
                              authAxios,
                              report_result_id: reportID,
                              save_type: "autosave",
                              content: fullTipTapJSON,
                            });

                            await authAxios.post(urls.docgen.moveReport, {
                              report_result_id: reportID,
                              sent_from: sentFrom,
                              sent_to: "writer",
                            });

                            toast.success(
                              "Report sent back to writer successfully.",
                            );
                            handleTransition(
                              openRow,
                              stage === "verify" ? "corr_rev" : "corr_ver",
                            );
                            setShowCommentsPanel(false);
                            history.push("/reports/report_module");
                          } catch (error: any) {
                            console.error("Failed to send report back:", error);
                            toast.error(
                              error?.response?.data?.error ||
                                "Failed to send back to writer.",
                            );
                          }
                        }}
                      >
                        <MI name="back" size={16} color="#fff" /> Send back to
                        writer
                      </button>
                    )}
                  </div>
                </div>
              </>
            );
          })()}
        </Box>
      </Drawer>
      {showFindReplace && (
        <FindReplaceWidget
          onClose={() => setShowFindReplace(false)}
          findText={findText}
          setFindText={setFindText}
          replaceText={replaceText}
          setReplaceText={setReplaceText}
          onExecuteReplace={handleExecuteFindReplace}
          matchCount={matches.length}
          currentIndex={currentMatchIndex}
          onNext={handleNextMatch}
          onPrev={handlePrevMatch}
        />
      )}
    </>
  );
}
